import { SignInCard } from "./SignInCard";
import { GuideLink } from "./GuideLink";
import { twelveHour } from "./clock";
import { Suspense, lazy, type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import type {
  OrganizerCodeChallenge,
  OrganizerSession,
  OrganizerWorkspaceCatalog,
  AttendeeConferenceReviewResponse,
  AttendeeRemovedAgendaItem,
  AttendeeLearningRecordResponse,
  PublicConventionGuide,
  PublicScheduleBlock,
  AttendeeLearningLibraryEntry,
  PublicScheduleItem,
} from "@verity-connect/contracts";
import { SignInLinkPage } from "./SignInLinkPage";
import { openInvitedDoor, resolveInvitedDoor } from "./invited-door";
import { signInExpiryNote } from "./sign-in-expiry";
import { SessionResources } from "./SessionResources";
import { BrandLockup } from "./BrandLockup";
import { ConventionMapPage } from "./ConventionMapPage";
import { HelpPage } from "./HelpPage";
import { PrivacyPage } from "./PrivacyPage";
import { DeviceLinkLanding } from "./DeviceLinkLanding";
import { WelcomeScreen, nativeShell, shouldShowWelcome, welcomeAlreadySeen, type WelcomeEvent } from "./WelcomeScreen";
import { useAttendeeStateEpoch } from "./cross-device-state";
import { firstName, greeting } from "./greeting";
import { readClientId } from "./personal";
import { refusalText } from "./sign-in-doors";
import { HELP_ANSWERS, WVCEA_EMAIL } from "./help-content";
import { associationPresentation, personalWorkspaceName } from "./association-presentation";
import { resourcesForItem, useSessionResources } from "./useSessionResources";
import { earnsContactHours, filterSchedule, findConventionPosition, normalizeSearchTerm, sanitizeNoteDocument, supersedesSearchTerm, type EventMode } from "@verity-connect/domain";
import { buildSearchableItems, itemLabel, loadGuide, roomLabel } from "./guide";
import { useAttendeeAccount, type AttendeeAccountState } from "./useAttendeeAccount";
import { usePersonalAgenda } from "./usePersonalAgenda";
import { useSchoolProposals } from "./useSchoolProposals";
import { usePrivateNotes } from "./usePrivateNotes";
import { markerLabel } from "./private-notes";
import { RecordSessionControl } from "./RecordSessionControl";
import { unarchivedUpdates } from "./update-read-state";
import { blockProgress, blockStates, dayTimeline, homeRows, orderedBlocks, scheduleDayId, sessionWorkspaceMode, toggleSavedItem, type DayTimelineRow, type RowState, type SessionWorkspaceMode } from "./day-timeline";
import { useUpdateReadState } from "./update-read-state";
import { useAttendeeUpdates } from "./useAttendeeUpdates";
import { usePushNotifications } from "./usePushNotifications";
import { useServiceReports } from "./useServiceReports";
import { AttendeeAccountPage } from "./AttendeeAccountPage";
import { DeleteAccountPage } from "./DeleteAccountPage";
import { JoinRoute } from "./JoinRoute";
import { ContinuingEducation } from "./ContinuingEducation";
import { MyRecordings } from "./MyRecordings";
import { PresenceLanding } from "./PresenceLanding";
import { PresenceSheet } from "./PresenceSheet";
import { flushPendingPresence, pendingPresenceCodes } from "./presence-pending";
import { LearningRecord } from "./LearningRecord";
import { SessionTranscript } from "./SessionTranscript";
import { StudySessionControl } from "./StudySessionControl";
import { ConferenceReview } from "./ConferenceReview";
import { Link, replaceSearch, useLocation } from "./router";
import { CheckInButton, usePresenceMarks } from "./CheckInButton";
import { StoreLanding, offerTheStores } from "./StoreLanding";
import type { LocationState } from "./router";
import { WeatherPill } from "./WeatherPill";
import { LearningCompanionDemo } from "./LearningCompanionDemo";
import { NoteEditor } from "./NoteEditor";
import { PlatformHandoff } from "./PlatformHandoff";
import { GuideEntry } from "./GuideEntry";
import { FederatedHandoff } from "./FederatedHandoff";
import { VerityConnectBrand } from "./VerityConnectBrand";
import { sessionFollowupKind, type SessionFollowupKind } from "./session-followup";
import { DemoEntry, DemoWorkspace, demoExperienceNow, isDemoHost, isInsideDemoPhone, useDemoClock } from "./DemoWorkspace";
import { AttendeeSurveys, SessionSurveyEntry } from "./AttendeeSurveys";
import { PersonalExperience } from "./PersonalExperience";
import { useAttendeePersonalHome } from "./useAttendeePersonalHome";
import { isPersonalRoute, participantEventMode, usesPersonalNavigation, type ConventionPhase } from "./participant-experience";
import { isPlatformHostname, tenantSurface, type TenantSurface } from "./app-host";
import { EventPartnerDetail, EventPartnerDirectory } from "./EventPartners";
import { AttendeeDesktopLanding, AttendeeDesktopToday, AttendeeLiveBrowserShell, AttendeePreparationHome, useDesktopAttendeeViewport } from "./AttendeeDesktop";
import { UpdateTheApplication } from "./UpdateTheApplication";
import { attendeeDesktopSurface, readAttendeeLiveContinuation, writeAttendeeLiveContinuation } from "./attendee-desktop";
import { mobileTakeoverDestination } from "./mobile-takeover";

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; guide: PublicConventionGuide; saved: boolean; priorEvent: boolean };

/**
 * The surfaces a teacher never opens.
 *
 * Organizer, school, presenter, exhibitor and the platform console are desktop work, and
 * together they were 55% of the code every phone downloaded before it could show a
 * schedule. Loaded on demand instead: somebody opening the organizer workspace on a
 * laptop waits a moment once, and six hundred teachers arriving at a convention on one
 * morning do not download it at all.
 *
 * The native applications only ever load attendee paths, so they never fetch any of this.
 */
const OrganizerWorkspace = lazy(async () => ({ default: (await import("./OrganizerWorkspace")).OrganizerWorkspace }));
const PresenterPortal = lazy(async () => ({ default: (await import("./PresenterPortal")).PresenterPortal }));
const ExhibitorPortal = lazy(async () => ({ default: (await import("./ExhibitorPortal")).ExhibitorPortal }));
const SchoolPortal = lazy(async () => ({ default: (await import("./SchoolPortal")).SchoolPortal }));
const SchoolEntry = lazy(async () => ({ default: (await import("./SchoolEntry")).SchoolEntry }));
const PlatformConsole = lazy(async () => ({ default: (await import("./PlatformConsole")).PlatformConsole }));
const FineArtsRolePortal = lazy(async () => ({ default: (await import("./FineArtsRolePortal")).FineArtsRolePortal }));
const PublicFineArtsFestival = lazy(async () => ({ default: (await import("./PublicFineArtsFestival")).PublicFineArtsFestival }));
const PublicFineArtsResults = lazy(async () => ({ default: (await import("./PublicFineArtsResults")).PublicFineArtsResults }));
const MobileExperienceApp = lazy(async () => ({ default: (await import("./MobileExperience")).MobileExperienceApp }));
const MobileEntry = lazy(async () => ({ default: (await import("./MobileExperience")).MobileEntry }));

/** Wraps a surface that is still arriving, so React has something to show meanwhile. */
function Loaded({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<SurfaceLoading />}>{children}</Suspense>;
}

/** Shown for the moment a desktop surface is arriving. Deliberately quiet. */
function SurfaceLoading() {
  return <div className="surface-loading" role="status">Loading…</div>;
}

export function App() {
  const location = useLocation();
  useEffect(() => {
    if (!nativeShell() || location.pathname === "/mobile-entry"
      || location.pathname === "/mobile" || location.pathname.startsWith("/mobile/")) return;
    const controller = new AbortController();
    void fetch("/api/mobile/experience", { credentials: "same-origin", signal: controller.signal })
      .then(async (response) => response.ok ? response.json() as Promise<import("@verity-connect/contracts").MobileExperienceManifest> : null)
      .then((manifest) => {
        const destination = manifest ? mobileTakeoverDestination(manifest, location.pathname) : null;
        if (destination) window.location.replace(destination);
      }).catch(() => { /* The locked v1 attendee shell remains the safe fallback. */ });
    return () => controller.abort();
  }, [location.pathname]);
  /* A mark taken in a room with no signal, or before she signed in, is held on the
     phone and goes out here — on every navigation, because there is no page she is
     guaranteed to visit and no moment she is guaranteed to be online. Costs nothing
     when the queue is empty, which is almost always. */
  useEffect(() => {
    if (location.pathname === "/present") return;
    if (pendingPresenceCodes().length === 0) return;
    void flushPendingPresence("");
  }, [location.pathname]);
  /* The application drawn inside the demo's phone marks the document, the way the
     native shell does. A browser inside that frame reports no notch and no home
     indicator, so without this the first heading slid under the island drawn over it
     and the frame carried a desktop scrollbar down its side. */
  const insidePhone = isInsideDemoPhone();
  useEffect(() => {
    if (!insidePhone) return;
    document.documentElement.dataset.demoPhone = "true";
    return () => { delete document.documentElement.dataset.demoPhone; };
  }, [insidePhone]);
  if (location.pathname === "/demo-entry") return <DemoEntry />;
  /*
   * A phone browser is offered the two stores rather than a lesser copy of the application.
   * Everything this product is for — a notification when a room moves, recording a seminar,
   * the card on the lock screen — is impossible in a browser on a phone, and thirty-four
   * teachers had signed in on one and never installed anything.
   *
   * Decided by the Worker, which marks the document from the User-Agent before a browser
   * sees it; the installed application's own web views are never marked. Three addresses
   * are never interrupted — see `offerTheStores`, and each of them breaks the convention.
   */
  if (offerTheStores(location.pathname)) return <StoreLandingPage />;
  /* Above every route, because the phone running an old application is on whichever page
     she happened to open, and there is no page she is guaranteed to visit. */
  const content = <><UpdateTheApplication /><RoutedApp location={location} /></>;
  if (!isDemoHost() || insidePhone) return content;
  return <DemoWorkspace pathname={location.pathname}>{content}</DemoWorkspace>;
}

function RoutedApp({ location }: { location: LocationState }) {
  if (location.pathname === "/mobile-entry") return <Loaded><MobileEntry /></Loaded>;
  if (location.pathname === "/mobile" || location.pathname.startsWith("/mobile/")) {
    return <Loaded><MobileExperienceApp pathname={location.pathname} /></Loaded>;
  }
  if (location.pathname === "/fine-arts") return <Loaded><PublicFineArtsFestival /></Loaded>;
  if (location.pathname === "/fine-arts-results") return <Loaded><PublicFineArtsResults /></Loaded>;
  if (location.pathname === "/fine-arts-role") {
    const hashToken = new URLSearchParams(window.location.hash.replace(/^#/u, "")).get("t");
    const token = hashToken ?? sessionStorage.getItem("wvcea:fine-arts-role") ?? "";
    return <Loaded><FineArtsRolePortal token={token} /></Loaded>;
  }
  if (isPlatformHost()) return <OrganizerApp pathname={location.pathname} search={location.search} platformMode />;
  if (location.pathname === "/verity-entry") return <PlatformHandoff />;
  if (location.pathname === "/guide-entry") return <GuideEntry />;
  if (location.pathname === "/federated-entry") return <FederatedHandoff />;
  if (location.pathname === "/demo/learning-companion") return <LearningCompanionDemo />;
  // Above the surface hand-off, because every host has to answer it. Google requires the
  // address it is given for account deletion to be readable by somebody with no account
  // and no app, and a page about leaving should not wait on the convention schedule
  // loading — least of all fail with it.
  if (location.pathname === "/delete-account") return <DeleteAccountPage />;
  /*
   * Where the square on a door lands, and it has to be above the surface hand-off.
   *
   * It sat below it, which meant no host ever reached it: wvcea.app is tagged the
   * public surface, the hand-off below answers first, and the guide's own router
   * has no such path, so every printed code led a teacher to "This page does not
   * exist." The engine behind it was right the whole time and nothing reached it.
   *
   * Above the sign-in door as well, and for the original reason: the session is
   * named before anybody is asked to sign in for it.
   */
  if (location.pathname === "/present") return <PresenceLanding />;
  const surface = tenantSurface();
  if (surface) return <TenantSurfaceApp surface={surface} location={location} />;
  // Checked before anything that loads a session, because the whole point of
  // these two paths is that nobody is signed in yet.
  if (location.pathname === "/staff/sign-in") {
    return <div className="organizer-shell"><SignInLinkPage audience="organizer" /></div>;
  }
  // A kept link, not a live door. Nothing issues attendee sign-in links any more, so
  // this lands on the one form that signs a teacher in rather than on a page waiting
  // for a token nobody can be holding.
  if (location.pathname === "/sign-in") return <JoinRoute audience="attendee" />;
  if (location.pathname === "/presenter/sign-in") {
    return <div className="organizer-shell"><SignInLinkPage audience="presenter" /></div>;
  }
  if (location.pathname === "/school/sign-in") {
    return <div className="organizer-shell"><SignInLinkPage audience="school" /></div>;
  }
  if (location.pathname === "/school/link") {
    return <div className="organizer-shell"><DeviceLinkLanding door="school" /></div>;
  }
  // Before each door's own app, because a phone reading a square has no session yet.
  if (location.pathname === "/presenter/link") {
    return <div className="organizer-shell"><DeviceLinkLanding door="presenter" /></div>;
  }
  if (location.pathname.startsWith("/presenter")) return <Loaded><PresenterPortal /></Loaded>;
  if (location.pathname.startsWith("/exhibitor")) return <Loaded><ExhibitorPortal /></Loaded>;
  if (location.pathname === "/staff/link") {
    return <div className="organizer-shell"><DeviceLinkLanding door="organizer" /></div>;
  }
  if (location.pathname === "/link") {
    return <div className="organizer-shell"><DeviceLinkLanding door="attendee" /></div>;
  }
  if (location.pathname === "/school") return <Loaded><SchoolEntry /></Loaded>;
  if (location.pathname.startsWith("/school/")) return <Loaded><SchoolPortal pathname={location.pathname.slice("/school".length)} /></Loaded>;
  // Its own address rather than a page inside the staff workspace: this is paper
  // for a printer, and the workspace's frame, navigation and colours are exactly
  // what must not come out of the printer with it.
  if (location.pathname === "/staff/presence-sheet") return <Loaded><PresenceSheet search={location.search} /></Loaded>;
  if (location.pathname.startsWith("/staff")) return <Loaded><OrganizerApp pathname={location.pathname.slice("/staff".length) || "/"} search={location.search} /></Loaded>;
  return <ParticipantApp location={location} />;
}

function TenantSurfaceApp({ surface, location }: { surface: TenantSurface; location: LocationState }) {
  if (surface === "public") return <ParticipantApp location={location} />;
  if (location.pathname === "/join") {
    const audience = surface === "staff" ? "staff" : surface;
    return <div className="app-shell"><main><JoinRoute audience={audience} /></main></div>;
  }
  if (surface === "staff") {
    // Same page as `/staff/presence-sheet` on the combined host. This host serves the
    // staff application from its own root, so the prefix is not there to match on, and
    // the first version of this route was reachable from exactly one of the two.
    if (location.pathname === "/presence-sheet") return <Loaded><PresenceSheet search={location.search} /></Loaded>;
    if (location.pathname === "/sign-in") return <div className="organizer-shell"><SignInLinkPage audience="organizer" /></div>;
    if (location.pathname === "/link") return <div className="organizer-shell"><DeviceLinkLanding door="organizer" /></div>;
    if (/^\/organizations\/[^/]+\/portal(?:\/|$)/u.test(location.pathname)) {
      return <Loaded><SchoolPortal pathname={location.pathname} /></Loaded>;
    }
    return <Loaded><OrganizerApp pathname={location.pathname} search={location.search} /></Loaded>;
  }
  if (surface === "school") {
    if (location.pathname === "/sign-in") return <div className="organizer-shell"><SignInLinkPage audience="school" /></div>;
    if (location.pathname === "/link") return <div className="organizer-shell"><DeviceLinkLanding door="school" /></div>;
    if (location.pathname === "/") return <Loaded><SchoolEntry /></Loaded>;
    return <Loaded><SchoolPortal pathname={location.pathname} /></Loaded>;
  }
  if (surface === "presenter") {
    if (location.pathname === "/sign-in") return <div className="organizer-shell"><SignInLinkPage audience="presenter" /></div>;
    if (location.pathname === "/link") return <div className="organizer-shell"><DeviceLinkLanding door="presenter" /></div>;
    return <Loaded><PresenterPortal /></Loaded>;
  }
  return <Loaded><ExhibitorPortal /></Loaded>;
}

function isPlatformHost(): boolean {
  return isPlatformHostname(window.location.hostname);
}

function ParticipantApp({ location }: { location: LocationState }) {
  const guideSignal = useAttendeeStateEpoch();
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [online, setOnline] = useState(navigator.onLine);
  const [now, setNow] = useState(() => new Date());
  const attendeeAccount = useAttendeeAccount();
  /* The shell's own contract, applied once.

     Until now the iOS shell reached into the page at document start and hid the web
     application's bottom navigation by class name, with a comment saying the better
     arrangement was for the application to recognise the shell instead. This is that:
     the shell declares itself, the application marks the document, and the stylesheet
     decides what a page inside a native frame looks like. A class name in Swift breaks
     silently the day the class is renamed; a declared platform does not. */
  useEffect(() => {
    const shell = nativeShell();
    if (!shell) return;
    document.documentElement.dataset.shell = shell.platform;
    return () => { delete document.documentElement.dataset.shell; };
  }, []);

  // Decided once, on the way in. Moving between pages afterwards must not bring it back,
  // and in a browser it never appears at all.
  const [showWelcome] = useState(() => shouldShowWelcome({
    pathname: location.pathname,
    inShell: nativeShell() !== null,
    alreadySeen: welcomeAlreadySeen(),
  }));

  useEffect(() => {
    if (guideSignal.epoch > 0 && guideSignal.reason === "write") return;
    const controller = new AbortController();
    setState((current) => current.status === "ready" ? current : { status: "loading" });
    void loadGuide(controller.signal)
      .then(({ guide, saved, priorEvent }) => setState({ status: "ready", guide, saved, priorEvent }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          status: "error",
          message: error instanceof Error ? error.message : "The official convention guide could not be loaded.",
        });
      });
    return () => controller.abort();
  }, [loadAttempt, guideSignal.epoch]);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    const clock = window.setInterval(() => setNow(new Date()), 60_000);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
      window.clearInterval(clock);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const heading = document.querySelector<HTMLElement>("[data-page-heading]");
    heading?.focus({ preventScroll: true });
    /*
     * Unless somebody asked for a particular block.
     *
     * The day rail names one — "Seminar 1 · 9 sessions" — and landing at the top of a
     * forty-three row day is the same as not having been taken anywhere. The heading still
     * takes focus, so a screen reader announces the page rather than a block out of
     * context, and then the block is brought into view.
     */
    const asked = new URLSearchParams(location.search).get("block");
    if (!asked) return;
    const target = document.getElementById(`block-${asked}`);
    target?.scrollIntoView({ block: "start" });
  }, [location.pathname, location.search]);

  const welcomeEvent: WelcomeEvent | null = state.status === "ready"
    ? {
      name: state.guide.event.name,
      themePrimary: state.guide.event.themePrimary,
      themeSecondary: state.guide.event.themeSecondary,
      // The dates alone. Adding the venue takes it onto a second line, and a teacher
      // holding a phone in the car park knows where they are.
      dateLine: `${formatEventDates(state.guide.event.startDate, state.guide.event.endDate)}, ${new Date(`${state.guide.event.startDate}T12:00:00Z`).getUTCFullYear()}`,
    }
    : null;
  // In front of every state, including the loading one, so the application opens on the
  // gorge rather than on the word "Loading".
  const welcome = showWelcome ? <WelcomeScreen event={welcomeEvent} /> : null;

  // Signing up does not need the schedule, so it does not wait for it. Somebody opening
  // the link their principal forwarded is not here to read the guide yet, and on a
  // struggling network — a venue car park, a school on a slow line — making them wait for
  // sixty kilobytes of convention before they can type their name is the same mistake the
  // old door made with email: a wait in front of the one thing they came to do. It also
  // means a guide that fails to load cannot close the door.
  const joining = location.pathname === "/join";
  if (joining) {
    return <div className="app-shell"><main><JoinRoute audience="attendee" /></main></div>;
  }
  if (state.status === "loading") return <>{welcome}<LoadingShell /></>;
  if (state.status === "error") {
    return <>{welcome}<ErrorShell message={state.message} retry={() => setLoadAttempt((value) => value + 1)} /></>;
  }
  if (state.priorEvent) return <>{welcome}<LastSavedGuide guide={state.guide} /></>;
  return <>{welcome}<ReadyParticipantApp state={state} online={online} now={now} location={location} attendeeAccount={attendeeAccount} /></>;
}

function LastSavedGuide({ guide }: { guide: PublicConventionGuide }) {
  return <div className="app-shell attendee-app-shell">
    <a className="skip-link" href="#main-content">Skip to main content</a>
    <Header pathname="/schedule" personalMode={false} eventName={guide.event.name} />
    <div className="attendee-content">
      <div className="offline-banner" role="status">Showing the last saved event. The current event could not be checked, so changes are off.</div>
      <main id="main-content"><Schedule guide={guide} search="" currentBlockId={null} nextBlockId={null} savedItemIds={[]} updateSavedItemIds={() => undefined} /></main>
    </div>
  </div>;
}

function ReadyParticipantApp({ state, online, now, location, attendeeAccount }: {
  state: Extract<LoadState, { status: "ready" }>;
  online: boolean;
  now: Date;
  location: LocationState;
  attendeeAccount: ReturnType<typeof useAttendeeAccount>;
}) {
  const session = attendeeAccount.state.status === "signed-in" ? attendeeAccount.state.session : null;
  const eventSession = session?.eventAccess.some((access) => access.eventId === state.guide.event.id) ? session : null;
  const agenda = usePersonalAgenda(eventSession, state.guide.event.slug, state.guide.items.map((item) => item.id), online);
  const schoolProposals = useSchoolProposals(eventSession, state.guide.event.slug, online);
  const privateNotes = usePrivateNotes(eventSession, state.guide.event.id, state.guide.event.slug, state.guide.items.map((item) => item.id), online);
  const attendeeUpdates = useAttendeeUpdates(eventSession, state.guide.event.slug, state.guide.updates, online);
  const personalHome = useAttendeePersonalHome(session);
  const guide = useMemo(() => ({ ...state.guide, updates: attendeeUpdates }), [state.guide, attendeeUpdates]);
  const demoClock = useDemoClock();
  const experienceNow = demoExperienceNow(demoClock, now);
  const updateReadState = useUpdateReadState(guide.updates);
  const position = conventionPosition(guide, experienceNow);
  const sessionResources = useSessionResources(eventSession !== null, state.guide.event.slug);
  // The demo moves the schedule clock into the convention dates. Its lifecycle clock
  // stays on real time, where the demo selector placed the active window.
  const mode = participantEventMode(now, guide.event.lifecycleWindows, position.phase);
  const personalMode = usesPersonalNavigation(session !== null, mode, location.pathname);
  const desktop = useDesktopAttendeeViewport();
  // What this phone is showing, and whether it can be reached. Neither is knowable from the
  // server's own records, and both are how a perfectly working morning goes wrong.
  useServiceReports(eventSession, state.guide.event.slug, state.guide.revision);
  const [continuedEventSlug, setContinuedEventSlug] = useState<string | null>(() =>
    readAttendeeLiveContinuation(state.guide.event.slug, browserSessionStorage()) ? state.guide.event.slug : null,
  );
  const continuedInBrowser = continuedEventSlug === guide.event.slug;
  const desktopSurface = attendeeDesktopSurface({
    desktop,
    nativeShell: nativeShell() !== null,
    mode,
    pathname: location.pathname,
    continuedInBrowser,
  });
  const continueInBrowser = () => {
    writeAttendeeLiveContinuation(guide.event.slug, true, browserSessionStorage());
    setContinuedEventSlug(guide.event.slug);
  };
  const leaveLiveBrowser = () => {
    writeAttendeeLiveContinuation(guide.event.slug, false, browserSessionStorage());
    setContinuedEventSlug(null);
  };
  const offlineMessage = !online
    ? "You are offline. Showing the saved Convention guide."
    : state.saved ? "Showing the last saved Convention guide while the live guide reconnects." : null;
  const route = (
    <RouteView
      guide={guide}
      pathname={location.pathname}
      sessionResources={sessionResources}
      search={location.search}
      currentBlockId={position.currentBlockId}
      nextBlockId={position.nextBlockId}
      phase={position.phase}
      now={experienceNow}
      savedItemIds={agenda.itemIds}
      removedFromSchedule={agenda.removed}
      updateSavedItemIds={agenda.updateItemIds}
      attendeeAccount={attendeeAccount}
      agendaSyncStatus={agenda.syncStatus}
      schoolProposals={schoolProposals}
      privateNotes={privateNotes}
      updateReadState={updateReadState}
      personalHome={personalHome}
      personalMode={personalMode}
      mode={mode}
    />
  );

  if (desktopSurface === "active-landing") {
    return <AttendeeDesktopLanding
      guide={guide}
      currentBlockId={position.currentBlockId}
      nextBlockId={position.nextBlockId}
      savedItemIds={agenda.itemIds}
      signedIn={session !== null}
      syncStatus={agenda.syncStatus}
      requestedPath={`${location.pathname}${location.search}`}
      offlineMessage={offlineMessage}
      onContinue={continueInBrowser}
    />;
  }

  if (desktopSurface === "live-browser") {
    const dayId = scheduleDayId(guide, position.currentBlockId, position.nextBlockId);
    const dayLabel = guide.days.find((day) => day.id === dayId)?.label ?? formatEventDates(guide.event.startDate, guide.event.endDate);
    return <AttendeeLiveBrowserShell
      guide={guide}
      pathname={location.pathname}
      search={location.search}
      dayLabel={dayLabel}
      offlineMessage={offlineMessage}
      onContinue={continueInBrowser}
      onBack={leaveLiveBrowser}
      onOpenPhone={leaveLiveBrowser}
    >
      {location.pathname === "/" ? <AttendeeDesktopToday
        guide={guide}
        currentBlockId={position.currentBlockId}
        nextBlockId={position.nextBlockId}
        now={experienceNow}
        savedItemIds={agenda.itemIds}
      /> : route}
    </AttendeeLiveBrowserShell>;
  }

  const standardRoute = desktopSurface === "preparation-home" ? <AttendeePreparationHome
    guide={guide}
    currentBlockId={position.currentBlockId}
    nextBlockId={position.nextBlockId}
    savedItemIds={agenda.itemIds}
    registered={eventSession !== null}
  /> : route;

  return (
    <div className={`app-shell attendee-app-shell${mode === "preparation" ? " attendee-preparation-shell" : ""}`}>
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <Header pathname={location.pathname} personalMode={personalMode} eventName={guide.event.name} personalLabel={mode === "preparation" ? "My WVCEA Connect" : undefined} />
      <div className="attendee-content">
        {offlineMessage && (
          <div className="offline-banner" role="status">
            {offlineMessage}
          </div>
        )}
        <main id="main-content">{standardRoute}</main>
      </div>
      <MobileNav pathname={location.pathname} personalMode={personalMode} />
    </div>
  );
}

function browserSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

type OrganizerAuthState =
  | { status: "loading" }
  | { status: "signed-out" }
  | { status: "requesting" }
  | { status: "code"; email: string; challengeId: string; expiresAt: string | null; developmentCode?: string; developmentSignInUrl?: string }
  | { status: "verifying"; email: string; challengeId: string; expiresAt: string | null; developmentCode?: string; developmentSignInUrl?: string }
  | { status: "signed-in"; session: OrganizerSession }
  | { status: "error"; message: string };

function OrganizerApp({ pathname, search, platformMode = false }: { pathname: string; search: string; platformMode?: boolean }) {
  const [auth, setAuth] = useState<OrganizerAuthState>({ status: "loading" });
  const [workspace, setWorkspace] = useState<OrganizerWorkspaceCatalog | null>(null);
  const [schedule, setSchedule] = useState<PublicConventionGuide | null>(null);
  /** Which event the guide in hand belongs to, so a refresh does not look like a move. */
  const loadedScheduleSlug = useRef<string | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduleReload, setScheduleReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    void fetch("/api/organizer/session", { signal: controller.signal, credentials: "same-origin" })
      .then(async (response) => {
        if (!response.ok) throw new Error(await responseMessage(response));
        const session = await response.json() as OrganizerSession | null;
        setAuth(session ? { status: "signed-in", session } : { status: "signed-out" });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setAuth({ status: "error", message: error instanceof Error ? error.message : "Your account could not be loaded." });
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (auth.status !== "signed-in" || platformMode) return;
    const controller = new AbortController();
    void fetch("/api/organizer/workspace", { signal: controller.signal, credentials: "same-origin" })
      .then(async (response) => {
        if (!response.ok) throw new Error(await responseMessage(response));
        setWorkspace(await response.json() as OrganizerWorkspaceCatalog);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setScheduleError(error instanceof Error ? error.message : "The staff workspace could not be loaded.");
      });
    return () => controller.abort();
  }, [auth.status, platformMode]);

  const routeEventSlug = pathname.match(/^\/staff\/events\/([^/]+)/u)?.[1];
  const requestedAssociationId = new URLSearchParams(search).get("association");
  const routeEvent = workspace?.events.find((event) => event.slug === routeEventSlug) ?? null;
  const selectedAssociation = workspace?.associations.find((association) => association.id === routeEvent?.associationId)
    ?? workspace?.associations.find((association) => association.id === requestedAssociationId)
    ?? workspace?.associations[0]
    ?? null;
  const selectedEvent = routeEventSlug
    ? routeEvent
    : workspace?.events.find((event) => event.associationId === selectedAssociation?.id) ?? null;
  useDocumentTitle(platformMode ? "Verity Connect" : selectedAssociation
    ? `Staff · ${selectedAssociation.appName}`
    : "Staff");
  const organizerAssociationQuery = requestedAssociationId
    ? `?association=${encodeURIComponent(requestedAssociationId)}`
    : "";

  useEffect(() => {
    if (auth.status !== "signed-in" || platformMode || !selectedEvent) return;
    const controller = new AbortController();
    // Emptying the guide unmounts the whole workspace, and every screen inside it loses
    // what somebody had chosen. That is right when moving to a different event and wrong
    // on a refresh of the one already open: adding a presenter refreshes the guide, and
    // the organizer was being thrown back to the first presenter in the list instead of
    // landing on the one they had just created.
    if (loadedScheduleSlug.current !== selectedEvent.slug) setSchedule(null);
    setScheduleError(null);
    void fetch(`/api/organizer/events/${encodeURIComponent(selectedEvent.slug)}/schedule`, {
      signal: controller.signal,
      credentials: "same-origin",
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(await responseMessage(response));
        setSchedule(await response.json() as PublicConventionGuide);
        loadedScheduleSlug.current = selectedEvent.slug;
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setScheduleError(error instanceof Error ? error.message : "The staff schedule could not be loaded.");
    });
    return () => controller.abort();
  }, [auth.status, scheduleReload, platformMode, selectedEvent?.slug]);

  const requestCode = async (email: string, humanCheck = "") => {
    setAuth({ status: "requesting" });
    try {
      const response = await fetch("/api/organizer/auth/code", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, "cf-turnstile-response": humanCheck }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const challenge = await response.json() as OrganizerCodeChallenge;
      if (!challenge.challengeId) {
        setAuth({ status: "error", message: "No active staff invitation was found for that email." });
        return;
      }
      setAuth({
        status: "code",
        email,
        challengeId: challenge.challengeId,
        expiresAt: challenge.expiresAt,
        ...(challenge.developmentCode ? { developmentCode: challenge.developmentCode } : {}),
        ...(challenge.developmentSignInUrl ? { developmentSignInUrl: challenge.developmentSignInUrl } : {}),
      });
    } catch (error) {
      setAuth({ status: "error", message: error instanceof Error ? error.message : "A sign-in code could not be requested." });
    }
  };

  const verifyCode = async (code: string) => {
    if (auth.status !== "code") return;
    const pending = auth;
    setAuth({ status: "verifying", email: pending.email, challengeId: pending.challengeId, expiresAt: pending.expiresAt, ...(pending.developmentCode ? { developmentCode: pending.developmentCode } : {}), ...(pending.developmentSignInUrl ? { developmentSignInUrl: pending.developmentSignInUrl } : {}) });
    try {
      const response = await fetch("/api/organizer/auth/verify", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        // Signing in here also opens a guide session, so the browser names itself.
        body: JSON.stringify({ challengeId: pending.challengeId, code, clientId: readClientId() }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      setAuth({ status: "signed-in", session: await response.json() as OrganizerSession });
    } catch (error) {
      setAuth({ status: "code", email: pending.email, challengeId: pending.challengeId, expiresAt: pending.expiresAt, ...(pending.developmentCode ? { developmentCode: pending.developmentCode } : {}), ...(pending.developmentSignInUrl ? { developmentSignInUrl: pending.developmentSignInUrl } : {}) });
      setScheduleError(error instanceof Error ? error.message : "The sign-in code could not be verified.");
    }
  };

  const signOut = async () => {
    await fetch("/api/organizer/logout", { method: "POST", credentials: "same-origin" });
    setWorkspace(null);
    setSchedule(null);
    setScheduleError(null);
    setAuth({ status: "signed-out" });
  };

  const signedIn = auth.status === "signed-in";
  return (
    <div className={`organizer-shell${signedIn ? " organizer-shell-authenticated" : ""}`}>
      <a className="skip-link" href="#organizer-main">Skip to staff workspace</a>
      {!signedIn && <header className={`organizer-topbar${platformMode ? " platform-auth-topbar" : ""}`}>
          {platformMode ? <Link className="platform-auth-brand" href="/"><VerityConnectBrand companyLine={false} /></Link> : <Link className="brand" href="/">
            <BrandLockup size="small" className="brand-lockup-header" />
          </Link>}
          <span className="organizer-label">{platformMode ? "Global admin" : "Staff"}</span>
        </header>}
      <div id={signedIn ? undefined : "organizer-main"} role={signedIn ? undefined : "main"} className={signedIn ? undefined : "organizer-main"}>
        {auth.status === "loading" && <OrganizerLoading />}
        {(auth.status === "signed-out" || auth.status === "requesting") && (
          <OrganizerEmailForm platformMode={platformMode} pending={auth.status === "requesting"} onSubmit={requestCode} />
        )}
        {(auth.status === "code" || auth.status === "verifying") && (
          <OrganizerCodeForm
            email={auth.email}
            expiresAt={auth.expiresAt}
            {...(auth.developmentCode ? { developmentCode: auth.developmentCode } : {})}
            {...(auth.developmentSignInUrl ? { developmentSignInUrl: auth.developmentSignInUrl } : {})}
            pending={auth.status === "verifying"}
            error={scheduleError}
            onSubmit={verifyCode}
            onBack={() => { setScheduleError(null); setAuth({ status: "signed-out" }); }}
            platformMode={platformMode}
          />
        )}
        {auth.status === "error" && (
          <section className="organizer-auth-card" aria-labelledby="organizer-error-title">
            {/* Named for the door somebody is actually standing at. This card called
                itself "Staff access" on every door, including the one into Verity
                Connect, which told the platform's owner he was an organizer of an
                association he does not belong to. */}
            <p className="organizer-kicker">{platformMode ? "Verity Connect" : "Staff access"}</p>
            {/* The message below now says what actually happened. A heading claiming the
                sign-in is unavailable contradicts it: a mistyped address is not the
                software being broken, and telling somebody it is sends them to support
                instead of to their own spelling. */}
            <h1 id="organizer-error-title" data-page-heading tabIndex={-1}>That did not sign you in</h1>
            <p role="alert">{auth.message}</p>
            <button type="button" className="organizer-primary" onClick={() => setAuth({ status: "signed-out" })}>Try again</button>
          </section>
        )}
        {auth.status === "signed-in" && (
          platformMode ? (
            auth.session.platformAdministrator
              ? <PlatformConsole session={auth.session} pathname={pathname} onSignOut={signOut} />
              : <section className="organizer-auth-card"><p className="organizer-kicker">Verity Connect</p><h1>This is not your workspace</h1><p>Verity Connect is where VerityWord runs the platform. Open your association&rsquo;s staff address to continue.</p></section>
          ) : routeEventSlug && workspace && !routeEvent ? (
            <section className="organizer-auth-card"><p className="organizer-kicker">Staff</p><h1>That event was not found</h1><p>Return to the event list and choose an event you can access.</p><Link className="organizer-primary" href={`/events${organizerAssociationQuery}`}>View events</Link></section>
          ) : workspace && selectedAssociation ? (
            <OrganizerWorkspace
              session={auth.session}
              workspace={workspace}
              association={selectedAssociation}
              event={selectedEvent}
              schedule={schedule}
              error={scheduleError}
              pathname={pathname}
              onSignOut={signOut}
              onScheduleChanged={() => setScheduleReload((value) => value + 1)}
            />
          ) : scheduleError ? (
            <section className="organizer-auth-card"><p className="organizer-kicker">Staff</p><h1>The workspace could not load</h1><p role="alert">{scheduleError}</p></section>
          ) : <OrganizerLoading />
        )}
      </div>
    </div>
  );
}

function OrganizerLoading() {
  return <section className="organizer-auth-card organizer-loading" aria-busy="true" aria-label="Loading staff access"><span /><span /><span /></section>;
}

/**
 * What a Google sign-in that did not finish left behind, said in words rather than in a
 * code. Somebody looking at an empty form after being bounced back deserves to know
 * which of these happened.
 */
const GOOGLE_SIGN_IN_NOTICES: Readonly<Record<string, string>> = {
  google_not_staff: "Verity Connect opens with a verityword.com account. Use your work Google account rather than a personal one.",
  google_not_administrator: "That account is recognized, but it is not set up to view every association. Your association workspace has everything you need.",
  google_no_account: "That Google account is not set up here yet.",
  google_expired: "That sign-in took too long. Start it again.",
  google_cancelled: "That sign-in was not finished.",
  google_failed: "Google could not be reached just now. The emailed code below still works.",
  google_unavailable: "Signing in with Google is not set up yet. Use the emailed code.",
};

function OrganizerEmailForm({ pending, onSubmit, platformMode }: { pending: boolean; platformMode: boolean; onSubmit: (email: string, humanCheck: string) => Promise<void> }) {
  const notice = GOOGLE_SIGN_IN_NOTICES[new URLSearchParams(window.location.search).get("sign_in") ?? ""];
  return (
    <>
      {!platformMode && <BrandLockup className="brand-lockup-card" />}
      <SignInCard
        door="organizer"
        title={platformMode ? "Verity Connect sign in" : "Staff sign in"}
        fieldLabel="Email address"
        fieldHint={platformMode ? "Your VerityWord work email." : "The address on your staff invitation."}
        submitLabel="Continue"
        pendingLabel="Requesting code…"
        pending={pending}
        intro={<>{notice && <p className="sign-in-notice" role="status">{notice}</p>}
      <div className="sign-in-google">
        <a className="sign-in-google-button" href={platformMode ? "/api/platform/auth/google/start" : "/api/organizer/auth/google/start"}>
          <GoogleMark />
          <span>Continue with Google</span>
        </a>
        <p>{platformMode
          ? "Staff open Verity Connect with their VerityWord account."
          : "Use the Google account on your staff invitation."}</p>
        {/* Said what the form below did when the form below emailed a code. It asks for an
            address and answers with a password now, and a code is only sent to somebody who
            asks for one, so the divider names the address rather than the mechanism. */}
        <span className="sign-in-google-or">{platformMode ? "or use an emailed code" : "or use your email address"}</span>
      </div></>}
        onSubmit={(email, humanCheck) => void onSubmit(email, humanCheck)}
        {...(platformMode ? {} : { onResolve: (email: string, check: string) => resolveInvitedDoor("organizer", email, check), onPassword: (email: string, password: string) => openInvitedDoor("organizer", email, password) })}
        footer={platformMode ? undefined : <GuideLink className="sign-in-footnote-link">Return to the convention guide</GuideLink>}
      />
    </>
  );
}

/** Drawn here rather than fetched. A sign-in screen should not depend on another
    server answering before somebody can press a button. */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
    </svg>
  );
}

function OrganizerCodeForm({ email, expiresAt, developmentCode, developmentSignInUrl, pending, error, onSubmit, onBack, platformMode }: {
  email: string;
  expiresAt: string | null;
  developmentCode?: string;
  developmentSignInUrl?: string;
  pending: boolean;
  error: string | null;
  onSubmit: (code: string) => Promise<void>;
  onBack: () => void;
  platformMode: boolean;
}) {
  const [code, setCode] = useState("");
  return (
    <section className="organizer-auth-card" aria-labelledby="organizer-code-title">
      {!platformMode && <BrandLockup className="brand-lockup-card" />}
      <p className="organizer-kicker">Check your email</p>
      <h1 id="organizer-code-title">Enter the six-digit code</h1>
      <p>The code went to <strong>{email}</strong>. {signInExpiryNote(expiresAt)}</p>
      {developmentCode && <p className="development-code" role="status">Local development code: <strong>{developmentCode}</strong></p>}
      {developmentSignInUrl && <p className="development-code"><a href={developmentSignInUrl}>Local development: tap to sign in instead</a></p>}
      {error && <p className="organizer-form-error" role="alert">{error}</p>}
      <form onSubmit={(event) => { event.preventDefault(); void onSubmit(code); }}>
        <label htmlFor="organizer-code">Sign-in code</label>
        <input id="organizer-code" name="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/gu, ""))} />
        <button className="organizer-primary" type="submit" disabled={pending}>{pending ? "Signing in…" : "Sign in"}</button>
        <button className="organizer-secondary" type="button" onClick={onBack}>Use another email</button>
      </form>
    </section>
  );
}

/**
 * Both halves of a refusal. A sign-in refusal now carries what happened and what to do
 * next, and showing only the first half is what left somebody with nothing to try.
 */
async function responseMessage(response: Response): Promise<string> {
  return refusalText(response);
}

function RouteView(props: {
  guide: PublicConventionGuide;
  pathname: string;
  sessionResources: ReturnType<typeof useSessionResources>;
  search: string;
  currentBlockId: string | null;
  nextBlockId: string | null;
  phase: ConventionPhase;
  now: Date;
  savedItemIds: readonly string[];
  /** Sessions she saved that are no longer on the schedule. */
  removedFromSchedule: readonly AttendeeRemovedAgendaItem[];
  updateSavedItemIds: (ids: readonly string[]) => void;
  attendeeAccount: ReturnType<typeof useAttendeeAccount>;
  agendaSyncStatus: "device" | "syncing" | "synced" | "offline";
  schoolProposals: ReturnType<typeof useSchoolProposals>;
  privateNotes: ReturnType<typeof usePrivateNotes>;
  updateReadState: ReturnType<typeof useUpdateReadState>;
  personalHome: ReturnType<typeof useAttendeePersonalHome>;
  personalMode: boolean;
  mode: EventMode;
}) {
  const detailMatch = props.pathname.match(/^\/schedule\/([^/]+)$/);
  const presenterMatch = props.pathname.match(/^\/more\/presenters\/([^/]+)$/);
  const exhibitorMatch = props.pathname.match(/^\/more\/exhibitors\/([^/]+)$/);
  const learningMatch = props.pathname.match(/^\/my-wvcea\/learning\/([^/]+)$/);
  // A page of her record rather than a separate thing, which is why it lives underneath it.
  const transcriptMatch = props.pathname.match(/^\/my-wvcea\/learning\/([^/]+)\/transcript$/);
  const surveyMatch = props.pathname.match(/^\/my-wvcea\/surveys\/([^/]+)$/);
  const personalRoute = isPersonalRoute(props.pathname);
  // The four sign-up links, and the walk-up door, all land on one form. Checked before
  // the personal routes below, because somebody arriving here has no session yet and
  // would otherwise be bounced to the sign-in screen this replaces.
  if (props.pathname === "/join") return <JoinRoute audience="attendee" />;
  // Somebody who is not signed in gets the one form, wherever they were heading. The
  // account screen still offers the mailed code this application stopped using, so a
  // teacher who followed a "sign in" link landed on a screen waiting for an email that
  // now never comes — and so would an App Store reviewer holding a password.
  //
  // Signed in, /account is her account: the place she changes things and deletes it.
  if (props.attendeeAccount.state.status !== "signed-in" && (personalRoute || props.pathname === "/account")) {
    return <JoinRoute audience="attendee" />;
  }
  if (props.pathname === "/account") return <AttendeeAccountPage state={props.attendeeAccount.state} signOut={props.attendeeAccount.signOut} eventSlug={props.guide.event.slug} />;
  if (props.attendeeAccount.state.status === "signed-in" && ((props.personalMode && props.pathname === "/") || personalRoute)) {
    return <PersonalExperience pathname={props.pathname} state={props.personalHome.state} retry={props.personalHome.retry} session={props.attendeeAccount.state.session} guide={props.guide} now={props.now} />;
  }
  if (props.pathname === "/my-wvcea/conference-review") {
    return <ConferenceReview guide={props.guide} signedIn={props.attendeeAccount.state.status === "signed-in"} />;
  }
  if (detailMatch?.[1]) {
    return <SessionDetail guide={props.guide} itemId={decodeURIComponent(detailMatch[1])} search={props.search} currentBlockId={props.currentBlockId} nextBlockId={props.nextBlockId} savedItemIds={props.savedItemIds} updateSavedItemIds={props.updateSavedItemIds} privateNotes={props.privateNotes} accountState={props.attendeeAccount.state} sessionResources={props.sessionResources} schoolProposals={props.schoolProposals} />;
  }
  if (presenterMatch?.[1]) return <PresenterDetail guide={props.guide} presenterId={decodeURIComponent(presenterMatch[1])} savedItemIds={props.savedItemIds} updateSavedItemIds={props.updateSavedItemIds} />;
  if (exhibitorMatch?.[1]) return <EventPartnerDetail eventSlug={props.guide.event.slug} eventName={props.guide.event.name} partnerId={decodeURIComponent(exhibitorMatch[1])} />;
  if (transcriptMatch?.[1]) {
    return <SessionTranscript guide={props.guide} itemId={decodeURIComponent(transcriptMatch[1])} signedIn={props.attendeeAccount.state.status === "signed-in"} />;
  }
  if (learningMatch?.[1]) {
    const itemId = decodeURIComponent(learningMatch[1]);
    return <LearningRecord guide={props.guide} item={props.guide.items.find((item) => item.id === itemId) ?? null} note={props.privateNotes.notes[itemId]} resourceState={props.sessionResources} resources={resourcesForItem(props.sessionResources, itemId)} signedIn={props.attendeeAccount.state.status === "signed-in"} />;
  }
  if (props.pathname === "/my-wvcea/surveys" || surveyMatch?.[1]) {
    return <AttendeeSurveys guide={props.guide} signedIn={props.attendeeAccount.state.status === "signed-in"} surveyId={surveyMatch?.[1] ? decodeURIComponent(surveyMatch[1]) : null} targetItemId={new URLSearchParams(props.search).get("target")} />;
  }
  if (props.pathname === "/schedule") return <Schedule guide={props.guide} search={props.search} currentBlockId={props.currentBlockId} nextBlockId={props.nextBlockId} savedItemIds={props.savedItemIds} updateSavedItemIds={props.updateSavedItemIds} schoolProposals={props.schoolProposals} />;
  if (props.pathname === "/my-wvcea/credit") {
    const account = props.attendeeAccount.state.status === "signed-in" ? props.attendeeAccount.state.session : null;
    return (
      <ContinuingEducation
        guide={props.guide}
        search={props.search}
        savedItemIds={props.savedItemIds}
        participant={account ? { displayName: account.displayName, email: account.email, personId: account.personId } : null}
      />
    );
  }
  // /my-stuff joins /my-convention as a name this page used to answer to. The web
  // application is already deployed, so a link somebody kept must still open.
  if (props.pathname === "/my-wvcea/information") return <ConventionInformation guide={props.guide} />;
  if (props.pathname === "/my-wvcea/learning") return <LearningRecords guide={props.guide} privateNotes={props.privateNotes} accountState={props.attendeeAccount.state} />;
  if (props.pathname === "/my-wvcea/recordings") return <MyRecordings guide={props.guide} signedIn={props.attendeeAccount.state.status === "signed-in"} />;
  if (props.pathname === "/my-wvcea/materials") return <SessionMaterialsPage guide={props.guide} resourceState={props.sessionResources} signedIn={props.attendeeAccount.state.status === "signed-in"} />;
  if (props.pathname === "/my-wvcea" || props.pathname === "/my-stuff" || props.pathname === "/my-convention") return <MyConvention mode={props.mode} guide={props.guide} savedItemIds={props.savedItemIds} accountState={props.attendeeAccount.state} syncStatus={props.agendaSyncStatus} privateNotes={props.privateNotes} resourceState={props.sessionResources} unreadUpdates={props.updateReadState.unreadCount} />;
  if (props.pathname === "/updates") return <UpdatesCenter updates={props.guide.updates} {...props.updateReadState} />;
  if (props.pathname === "/map" || props.pathname === "/explore") return <ConventionMapPage guide={props.guide} search={props.search} />;
  if (props.pathname === "/help") return <HelpPage eventName={props.guide.event.name} />;
  // Reachable without signing in, because somebody deciding whether to make an account is
  // exactly the person who wants to read it. Both app stores also require the address.
  if (props.pathname === "/privacy") return <PrivacyPage eventName={props.guide.event.name} />;
  if (props.pathname === "/more/presenters") return <Presenters guide={props.guide} />;
  if (props.pathname === "/more/exhibitors") return <EventPartnerDirectory eventSlug={props.guide.event.slug} eventName={props.guide.event.name} />;
  if (props.pathname === "/more/rooms") return <Rooms guide={props.guide} />;
  if (props.pathname === "/more/event") return <EventDetails guide={props.guide} />;
  if (props.pathname === "/more") return <More guide={props.guide} accountState={props.attendeeAccount.state} unreadUpdates={props.updateReadState.unreadCount} />;
  if (props.pathname === "/") {
    const account = props.attendeeAccount.state.status === "signed-in" ? props.attendeeAccount.state.session : null;
    return <EventHome {...props} displayName={account?.displayName ?? null} unreadUpdates={props.updateReadState.unreadCount} />;
  }
  return <NotFound />;
}

/**
 * The bar across the top.
 *
 * The logo went on 17 August. It named the application to somebody already inside it,
 * and took the top of every screen to do so. The page's own heading says where you
 * are, which is the part that changes.
 */
function Header({ pathname, personalMode = false, eventName, personalLabel }: { pathname: string; personalMode?: boolean; eventName: string; personalLabel?: string | undefined }) {
  const presentation = associationPresentation();
  const personalName = personalWorkspaceName();
  return (
    <header className="topbar">
      <Link className="brand" href={personalMode ? "/home" : "/"} aria-label={`${presentation.appName} home`}><BrandLockup size="small" className="brand-lockup-header" /></Link>
      <div className="desktop-workspace-context"><span>{personalMode ? "Your account" : "Attendee workspace"}</span><strong>{personalMode ? personalName : eventName}</strong></div>
      <PrimaryNav className="desktop-nav" pathname={pathname} personalMode={personalMode} personalLabel={personalLabel} />
      <Link className="help-button" href="/help" aria-label="Help" aria-current={pathname === "/help" ? "page" : undefined}><span aria-hidden="true">?</span></Link>
    </header>
  );
}

function MobileNav({ pathname, personalMode = false }: { pathname: string; personalMode?: boolean }) {
  return <PrimaryNav className="mobile-nav" pathname={pathname} personalMode={personalMode} />;
}

function PrimaryNav({ className, pathname, personalMode = false, personalLabel }: { className: string; pathname: string; personalMode?: boolean; personalLabel?: string | undefined }) {
  const personalName = personalLabel ?? personalWorkspaceName();
  if (personalMode) return (
    <nav className={className} aria-label="Primary navigation">
      <NavLink href="/home" label="Home" icon="home" active={pathname === "/" || pathname === "/home"} />
      <NavLink href="/messages" label="Messages" icon="messages" active={pathname === "/messages"} />
      <NavLink href="/events" label="Events" icon="schedule" active={pathname === "/events"} />
      <NavLink href="/records" label="Records" icon="personal" active={pathname.startsWith("/records")} />
      <NavLink href="/account" label="Account" icon="person" active={pathname === "/account"} />
    </nav>
  );
  if (className === "desktop-nav") return (
    <nav className={className} aria-label="Primary navigation">
      <NavLink href="/" label="Home" icon="home" active={pathname === "/"} />
      <NavLink href="/schedule" label="Plan" icon="schedule" active={pathname.startsWith("/schedule")} />
      <NavLink href="/more" label="Explore" icon="map" active={pathname.startsWith("/more") || pathname === "/map" || pathname === "/explore"} />
      <NavLink href="/my-wvcea" label={personalName} icon="person" active={pathname.startsWith("/my-wvcea") || pathname.startsWith("/my-stuff") || pathname.startsWith("/my-convention")} />
      <NavLink href="/updates" label="Updates" icon="updates" active={pathname === "/updates"} />
    </nav>
  );
  return (
    <nav className={className} aria-label="Primary navigation">
      <NavLink href="/" label="Home" icon="home" active={pathname === "/"} />
      <NavLink href="/schedule" label="Schedule" icon="schedule" active={pathname.startsWith("/schedule")} />
      {/* Five identical tabs. My WVCEA was raised on 17 August for where the product is
          going rather than what it is used for today; in the hand it read as a button
          floating above the bar rather than a place inside it. The selected tab is the
          one drawn filled, which is the only difference between any of them. */}
      <NavLink href="/my-wvcea" label={personalName} icon="person" active={pathname.startsWith("/my-wvcea") || pathname.startsWith("/my-stuff") || pathname.startsWith("/my-convention")} />
      <NavLink href="/map" label="Map" icon="map" active={pathname === "/map" || pathname === "/explore"} />
      <NavLink href="/more" label="More" icon="more" active={pathname.startsWith("/more") || pathname === "/updates"} />
    </nav>
  );
}

 type IconName = "person" | "home" | "schedule" | "personal" | "map" | "more" | "messages" | "search" | "people" | "document" | "rooms" | "info" | "parking" | "bookmark" | "updates" | "note" | "review" | "survey" | "filters" | "microphone";

function NavLink({ href, label, icon, active, className }: { href: string; label: string; icon: IconName; active: boolean; className?: string }) {
  return <Link href={href} className={className} aria-current={active ? "page" : undefined}><AppIcon name={icon} /><span>{label}</span></Link>;
}

function AppIcon({ name }: { name: IconName }) {
  if (name === "home") return <svg className="icon-home" aria-hidden="true" viewBox="0 0 24 24"><path d="m3 11 9-8 9 8v9h-6v-6H9v6H3z" /></svg>;
  if (name === "schedule") return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M5 3v3m14-3v3M4 8h16M4 5h16v16H4zM8 12h3m2 0h3m-8 4h3m2 0h3" /></svg>;
  if (name === "personal") return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M6 3h12v18l-6-4-6 4zM9 9h6m-3-3v6" /></svg>;
  if (name === "map") return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="m3 6 6-3 6 3 6-3v15l-6 3-6-3-6 3zM9 3v15m6-12v15" /></svg>;
  if (name === "more") return <svg className="icon-more" aria-hidden="true" viewBox="0 0 24 24"><circle cx="5" cy="12" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="19" cy="12" r="1.5" /></svg>;
  if (name === "messages") return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M4 5h16v12H8l-4 4zM8 9h8m-8 4h6" /></svg>;
  if (name === "people") return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M9 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 9c0-4 3-7 7-7s7 3 7 7m2-16a3 3 0 0 1 1 6m1 3c2 .8 3 2.7 3 5" /></svg>;
  if (name === "document") return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M6 3h8l4 4v14H6zM14 3v5h5M9 12h6m-6 4h6" /></svg>;
  if (name === "rooms") return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M4 3h16v18H4zM8 7h8v10H8zm6 5h.01" /></svg>;
  if (name === "info") return <svg aria-hidden="true" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 11v6m0-10h.01" /></svg>;
  if (name === "parking") return <svg aria-hidden="true" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M9 17V7h4a3 3 0 0 1 0 6H9" /></svg>;
  if (name === "bookmark") return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M6 3h12v18l-6-4-6 4z" /></svg>;
  if (name === "updates") return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M12 3a6 6 0 0 0-6 6v4l-2 3h16l-2-3V9a6 6 0 0 0-6-6Zm-2 16a2 2 0 0 0 4 0" /></svg>;
  // A page being written on, for the record a teacher makes out of a session.
  if (name === "note") return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M6 3h8l4 4v8m-12 6h-1V3m1 18h9M9 9h4m-4 4h3m8.5 1.5-6 6L14 22l.5-2.5 6-6a1.4 1.4 0 0 1 2 2Z" /></svg>;
  // One thing gathered out of many, for the wrap-up across the whole convention.
  if (name === "review") return <svg aria-hidden="true" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="3.4" /></svg>;
  // A form with an answer on it.
  /* Three sliders with their knobs on them, which is what the drawing shows and what
     every application means by filters. The first attempt was two rails with a tick
     across each, and at icon size that reads as a smudge rather than a control. */
  if (name === "filters") return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M3 6h6m4 0h8M3 12h11m4 0h3M3 18h4m4 0h13" /><circle cx="11" cy="6" r="2" /><circle cx="16" cy="12" r="2" /><circle cx="9" cy="18" r="2" /></svg>;
  if (name === "survey") return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M7 4h10v17H7zM9.5 9l1.5 1.5L14 7.5M9.5 15l1.5 1.5L14 13.5" /></svg>;
  if (name === "microphone") return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M12 3a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3zM6 11a6 6 0 0 0 12 0M12 17v4m-3 0h6" /></svg>;
  // A person, for the tab that is a person's own. "personal" is a bookmark with a plus
  // in it, which was right when the tab was called My Stuff and is not right now.
  if (name === "person") return <svg aria-hidden="true" viewBox="0 0 24 24"><circle cx="12" cy="8" r="3.6" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" /></svg>;
  return <svg aria-hidden="true" viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="m16 16 5 5" /></svg>;
}

function EventHome({ guide, currentBlockId, nextBlockId, now, savedItemIds, removedFromSchedule, privateNotes, displayName, unreadUpdates }: {
  guide: PublicConventionGuide;
  currentBlockId: string | null;
  nextBlockId: string | null;
  /** Drives the bar under the running block; the shell reissues it every minute. */
  now: Date;
  savedItemIds: readonly string[];
  removedFromSchedule: readonly AttendeeRemovedAgendaItem[];
  privateNotes: ReturnType<typeof usePrivateNotes>;
  /** Null until somebody links their account; the page greets the hour regardless. */
  displayName: string | null;
  unreadUpdates: number;
}) {
  useDocumentTitle(guide.event.name);
  const current = guide.blocks.find((block) => block.id === currentBlockId);
  const next = guide.blocks.find((block) => block.id === nextBlockId);
  const latestUpdate = guide.updates[0];
  const savedSet = new Set(savedItemIds);
  const currentItems = current?.itemIds.map((id) => guide.items.find((item) => item.id === id)).filter(isItem) ?? [];
  const currentItem = currentItems.find((item) => savedSet.has(item.id)) ?? (currentItems.length === 1 ? currentItems[0] : undefined);
  const activeDay = guide.days.find((day) => day.id === scheduleDayId(guide, currentBlockId, nextBlockId));
  // The whole day, not only what has been saved. A seminar row becomes a session
  // as soon as one is chosen; everything else is fixed for everybody.
  const timeline = activeDay ? dayTimeline(guide, savedSet, activeDay.id, currentBlockId, nextBlockId, removedFromSchedule) : [];
  const shownRows = homeRows(timeline, 5);
  // Exactly one row gets the card: whatever is running, or the next thing when the
  // day has not reached anything yet. Two cards would make the day read as two
  // competing things to look at.
  const focusRow = timeline.find((row) => row.state === "now") ?? timeline.find((row) => row.state === "next") ?? null;
  /*
   * The evening, and her record of the day.
   *
   * Once today's last block has ended, the card that carried "happening now" all day has
   * nothing to say, and this is the one moment a gap in her check-ins can still be fixed by
   * her. It never says "you missed one" — she may have checked in to a room she never saved —
   * only the two numbers, and the one place to act. Gone at midnight by the convention's clock.
   */
  const marks = usePresenceMarks(guide.event.slug);
  const todayDate = new Intl.DateTimeFormat("en-CA", { timeZone: guide.event.timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  const todayDay = guide.days.find((day) => day.date === todayDate);
  const todayRows = todayDay ? dayTimeline(guide, savedSet, todayDay.id, currentBlockId, nextBlockId, removedFromSchedule) : [];
  const dayIsOver = todayRows.length > 0 && todayRows.every((row) => row.state === "past");
  const savedSeminarsToday = todayRows.filter((row) => row.chosen && row.itemId
    && guide.items.find((item) => item.id === row.itemId)?.kind === "instructional");
  const checkedInToday = marks ? savedSeminarsToday.filter((row) => marks.some((mark) => mark.itemId === row.itemId)).length : 0;
  const recordToday = marks !== null && dayIsOver && savedSeminarsToday.length > 0 && checkedInToday < savedSeminarsToday.length
    ? { saved: savedSeminarsToday.length, checkedIn: checkedInToday }
    : null;
  const notedItems = Object.values(privateNotes.notes)
    .filter((note) => note.values.body.trim() || note.values.markers.length > 0)
    .sort((left, right) => right.savedAt.localeCompare(left.savedAt));
  const recentNote = notedItems[0];
  const recentNoteItem = recentNote ? guide.items.find((item) => item.id === recentNote.itemId) : undefined;
  return (
    <div className="home-page">
      {/* The convention's own name stood here until 17 August, announced at somebody
          who had just opened the convention's own application. What a teacher does not
          know on arriving is the hour and what is next, so the page opens on those. */}
      <header className="home-greeting">
        <div className="home-greeting-top">
          <h1 data-page-heading tabIndex={-1}>{displayName ? `${greeting(now)}, ${firstName(displayName)}` : greeting(now)}</h1>
          <Link className="home-bell" href="/updates" aria-label={unreadUpdates > 0 ? `Updates, ${unreadUpdates} unread` : "Updates"}>
            <AppIcon name="updates" />
            {unreadUpdates > 0 && <span className="home-bell-count" aria-hidden="true">{unreadUpdates > 9 ? "9+" : unreadUpdates}</span>}
          </Link>
        </div>
        <div className="home-greeting-line">
          <p>{activeDay?.label ?? formatEventDates(guide.event.startDate, guide.event.endDate)}</p>
          <WeatherPill eventSlug={guide.event.slug} timeZone={guide.event.timeZone} dayLabel={activeDay?.label ?? "Convention day"} />
        </div>
      </header>

      {/* Lifted out of the rail below on 17 August. Inside the list it was one row
          among many; the next thing to walk to earns the top of the screen. */}
      {recordToday && (
        <section className="home-record-today" aria-labelledby="home-record-today-title">
          <p className="home-record-today-label">Today on your record</p>
          <h2 id="home-record-today-title">
            You checked in to {recordToday.checkedIn} of the {recordToday.saved} seminars you saved.
          </h2>
          <p>If you were in a seminar you did not check in to, add it on your voucher.</p>
          <Link className="rail-focus-primary" href="/my-wvcea/credit?attention=today">Open my voucher</Link>
        </section>
      )}
      {focusRow && <section className="home-next-up" aria-label="Next session">
        <FocusCard row={focusRow} now={now} timeZone={guide.event.timeZone} eventSlug={guide.event.slug}
          checkIn={guide.items.find((item) => item.id === focusRow.itemId)?.kind === "instructional"} />
      </section>}

      {/* Only the ones that cannot wait for a tap, and only as a line.
          An ordinary update already has a place: the bell at the top of this screen, which
          carries the number of unread ones and opens them. Printing the whole of the newest
          one here as well filled the first screen of the convention with a message a teacher
          had just been sent, above her next session and above her day. A room that has moved
          under somebody is different, because she is already walking to the old one — so it
          keeps a line here, saying what it is and what it is about, and nothing more. */}
      {latestUpdate && latestUpdate.priority === "urgent" && (
        <Link className="home-update-badge" href={latestUpdate.targetPath ?? "/updates"}>
          <span aria-hidden="true"><AppIcon name="info" /></span>
          <span><small>Urgent update</small><strong>{latestUpdate.title}</strong></span>
          <b aria-hidden="true">›</b>
        </Link>
      )}

      <div className="home-convention-grid">
        <section className="home-day" aria-labelledby="home-day-heading">
          <header>
            <h2 id="home-day-heading">Your day</h2>
          </header>
          <ol className="day-rail">
            {shownRows.map((row) => (
              <li key={row.key} className={`rail-row is-${row.state}${row.awaitingChoice ? " is-open" : ""}${row.chosen ? " is-chosen" : ""}`}>
                <time dateTime={row.startTime}>{formatClock12(row.startTime)}</time>
                <span className="rail-dot" aria-hidden="true" />
                <Link className="rail-body" href={row.href}>
                  {row.chosen && <em>Your selection</em>}
                  {/*
                    * A session she chose that was taken off the schedule says so in
                    * its own words. The open-choice wording below is written for a
                    * slot nobody has answered, and it was swallowing this row whole:
                    * "Seminar 1 · 9 sessions — Choose your session" is exactly what
                    * a teacher saw after a presenter withdrew, which is the fault
                    * this row exists to fix.
                    */}
                  {row.chosen && <span className="rail-mark" aria-hidden="true"><AppIcon name="bookmark" /></span>}
                  {/*
                    * A slot whose session was removed reads exactly like any other
                    * slot she has not answered — the same heading, the same call to
                    * action — with one line at the foot saying why it is open again.
                    * The removed session is not named: she chose it, she knows what
                    * it was, and putting a seminar nobody is giving at the top of
                    * the row tells her the opposite of what to do.
                    */}
                  <strong>{row.awaitingChoice ? `${row.title} · ${row.choiceCount} sessions` : row.title}</strong>
                  <small>{row.awaitingChoice
                    ? <>Choose your session <b aria-hidden="true">→</b></>
                    : row.detail}</small>
                  {row.removedTitle && <small className="rail-removed-title">Your session was removed</small>}
                </Link>
              </li>
            ))}
          </ol>
          <Link className="home-day-full" href="/schedule?day=mine">View full schedule <b aria-hidden="true">→</b></Link>
        </section>

        <section className="learning-companion-home" aria-labelledby="learning-companion-heading">
          <header><div><h2 id="learning-companion-heading">Learning Companion</h2><p>Keep the ideas you want to carry home.</p></div><span aria-hidden="true">✎</span></header>
          {recentNote && recentNoteItem ? <div className="learning-companion-note"><small>{notedItems.length} {notedItems.length === 1 ? "session" : "sessions"} with notes</small><strong>{recentNoteItem.title}</strong><p>{recentNote.values.body.trim() || recentNote.values.markers.find((marker) => marker.text.trim())?.text}</p><Link href={`/schedule/${encodeURIComponent(recentNoteItem.id)}?view=notes`}>Continue this note <b aria-hidden="true">›</b></Link></div> : <div className="learning-companion-empty"><Link href={currentItem ? `/schedule/${encodeURIComponent(currentItem.id)}?view=notes` : "/schedule"}>{currentItem ? "Open this session’s notes" : "Find a session"} <b aria-hidden="true">›</b></Link></div>}
        </section>
      </div>

      {next && current && <section className="home-next" aria-labelledby="home-next-heading"><div className="section-heading"><h2 id="home-next-heading">Up next</h2><Link className="text-link" href="/schedule">Full schedule</Link></div><BlockSummary guide={guide} block={next} label="Next block" /></section>}
    </div>
  );
}

/**
 * Every notice WVCEA sent, kept rather than consumed.
 *
 * Archiving hides a notice at the bottom of the page; it never deletes one. The
 * association sent it and its record of sending stands, so the most an attendee can do
 * is put it away — and take it out again when they cannot remember which car park.
 */
function UpdatesCenter({ updates, unreadIds, unreadCount, markRead, markAllRead, archivedIds, setArchived }: {
  updates: readonly PublicConventionGuide["updates"][number][];
  unreadIds: readonly string[];
  unreadCount: number;
  markRead: (id: string) => void;
  markAllRead: () => void;
  archivedIds: ReadonlySet<string>;
  setArchived: (id: string, archived: boolean) => void;
}) {
  useDocumentTitle(`Updates · ${associationPresentation().appName}`);
  /*
   * Unread first, because that is what she came for.
   *
   * Landing on "All" meant a teacher opening Updates to read the one thing she had been
   * told about had to find it among everything the convention had ever said. Falls back to
   * All when there is nothing unread, so the page is never an empty list under a filter she
   * did not choose.
   */
  const [filter, setFilter] = useState<"all" | "unread">(() => (unreadIds.length > 0 ? "unread" : "all"));
  const unreadSet = new Set(unreadIds);
  const current = unarchivedUpdates(updates, archivedIds);
  const archived = updates.filter((update) => archivedIds.has(update.id));
  const shown = filter === "unread" ? current.filter((update) => unreadSet.has(update.id)) : current;
  /*
   * Nothing is open until she opens it.
   *
   * This used to open the newest update the moment the screen loaded, which put the list
   * and a wall of text on the same phone screen: the row she was meant to tap sat above
   * the message it would have shown her. It also marked that update read without her
   * having read a word of it, so the Unread filter emptied itself on arrival.
   *
   * One thing at a time. The list, or the message she chose, with a way back.
   */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = selectedId === null ? null : updates.find((update) => update.id === selectedId) ?? null;
  const choose = (id: string) => { setSelectedId(id); markRead(id); };
  return (
    <div className="page updates-page">
      <section className="updates-heading">
        <div><h1 data-page-heading tabIndex={-1}>Updates</h1>{unreadCount > 0 && <span>{`${unreadCount} unread ${unreadCount === 1 ? "update" : "updates"}`}</span>}</div>
        {unreadCount > 0 && <button type="button" onClick={markAllRead}>Mark all read</button>}
      </section>
      {updates.length === 0 ? (
        <section className="updates-empty"><span aria-hidden="true">✓</span><h2>No official updates</h2><Link href="/">Return home</Link></section>
      ) : (
        <section className="updates-workbench" aria-label="Official updates">
          {selected === null && <div className="updates-inbox">
            <div className="updates-filters" role="group" aria-label="Filter updates">
              <button type="button" aria-pressed={filter === "all"} onClick={() => setFilter("all")}>All <b>{current.length}</b></button>
              <button type="button" aria-pressed={filter === "unread"} onClick={() => setFilter("unread")}>Unread <b>{unreadCount}</b></button>
            </div>
            {/* Every update gets a row, including the only one. Hiding the list at one
                update saved a line of screen and cost the page its shape: a teacher who
                opened Updates met a wall of text with nothing to tap, and the rows she had
                been promised appeared only once a second message arrived. A list of one is
                still the list. */}
            {shown.length === 0 ? <p className="updates-caught-up">You have read every official update.</p> : <div className="updates-list">{shown.map((update) => (
              <button key={update.id} type="button" className={`${unreadSet.has(update.id) ? "unread " : ""}${update.priority === "urgent" ? "urgent" : ""}`} onClick={() => choose(update.id)}>
                {/* The title alone. Every message was printed in full in its own row and
                    again in the panel beside it, so a page of six updates was six walls of
                    text and the one she wanted was somewhere inside them. */}
                <span aria-hidden="true" /><strong>{update.title}</strong>
              </button>
            ))}</div>}
          </div>}
          {selected && <article className={`update-detail${selected.priority === "urgent" ? " urgent" : ""}`}>
            <button type="button" className="update-back" onClick={() => setSelectedId(null)}><b aria-hidden="true">‹</b> All updates</button>
            <p>{updateCategoryLabel(selected.category)}</p><h2>{selected.title}</h2><time dateTime={selected.publishedAt}>{formatUpdateTime(selected.publishedAt)}</time><div>{selected.body}</div>
            {selected.targetPath && <Link className="update-detail-action" href={selected.targetPath}>Open affected session <b aria-hidden="true">›</b></Link>}
            <button type="button" className="update-archive" onClick={() => setArchived(selected.id, !archivedIds.has(selected.id))}>{archivedIds.has(selected.id) ? "Put back in the list" : "Archive"}</button>
            <footer><span aria-hidden="true">✓</span><p>This is the canonical in-app record of this message.</p></footer>
          </article>}
        </section>
      )}
      {archived.length > 0 && <details className="updates-archived">
        <summary>Archived <b>{archived.length}</b></summary>
        <div className="updates-list">{archived.map((update) => (
          <div key={update.id} className="updates-archived-row">
            <span><time dateTime={update.publishedAt}>{formatUpdateTime(update.publishedAt)}</time><strong>{update.title}</strong></span>
            <button type="button" onClick={() => setArchived(update.id, false)}>Put back</button>
          </div>
        ))}</div>
      </details>}
    </div>
  );
}

function formatUpdateTime(value: string): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(value));
}

function updateCategoryLabel(value: PublicConventionGuide["updates"][number]["category"]): string {
  if (value === "room_change") return "Room change";
  if (value === "schedule_change") return "Schedule change";
  if (value === "reminder") return "Reminder";
  return "Official association update";
}

/* The line under a row is for something the row cannot say by itself — a count, or an
   unread number. A row called Help does not also need telling that it answers questions. */
function ActionRow({ href, icon, title, detail }: { href: string; icon: IconName; title: string; detail: string }) {
  return <Link href={href}><span className="action-icon"><AppIcon name={icon} /></span><span><strong>{title}</strong>{detail && <small>{detail}</small>}</span><b aria-hidden="true">›</b></Link>;
}

function BlockSummary({ guide, block, label, current = false }: { guide: PublicConventionGuide; block: PublicScheduleBlock; label: string; current?: boolean }) {
  const day = guide.days.find((candidate) => candidate.id === block.dayId);
  const items = block.itemIds.map((id) => guide.items.find((item) => item.id === id)).filter(isItem);
  const singleItem = items.length === 1 ? items[0] : undefined;
  const presenterNames = singleItem?.presenterIds.map((id) => guide.presenters.find((presenter) => presenter.id === id)?.displayName).filter(isString) ?? [];
  const title = singleItem?.title ?? block.title;
  const href = singleItem ? `/schedule/${encodeURIComponent(singleItem.id)}` : `/schedule?day=${encodeURIComponent(block.dayId)}`;
  return (
    <article className={`block-summary${current ? " current" : ""}`}>
      <div className="time-badge"><strong>{formatTime(block.startTime).replace(/ [ap]\.m\.$/, "")}</strong><span>{block.startTime < "12:00" ? "AM" : "PM"}</span></div>
      <div className="block-copy"><p className="status-kicker">{label}</p><h3>{title}</h3>{presenterNames.length > 0 && <p>{presenterNames.join(", ")}</p>}<small><time dateTime={`${day?.date ?? ""}T${block.startTime}`}>{day?.label} · {formatTimeRange(block.startTime, block.endTime)}</time></small></div>
      <Link aria-label={`Open ${title}`} href={href}>›</Link>
    </article>
  );
}

/**
 * The three chips the mockup draws, over the fifteen labels WVCEA publishes.
 *
 * A teacher choosing a seminar asks "is this for my age group", and WVCEA answers in
 * fifteen ways — Elementary, Grades 3–6, PreK–8th Grade, Preschool–Lower Elementary,
 * K4/K5 and so on. Three chips cover that question; the exact fifteen stay in the filter
 * panel for anybody who wants one of them.
 *
 * This is a grouping we derive, not a category WVCEA published, so it is stated here in
 * one place rather than spread across the interface. An audience may belong to more than
 * one — "Secondary • Administration" answers to both — which is correct: a person
 * looking for either should find it.
 */
const AUDIENCE_GROUPS: readonly { id: string; label: string; matches: RegExp }[] = [
  { id: "elementary", label: "Elementary", matches: /elementary|grades?\s*3|prek|pre-k|preschool|k4|k5/i },
  { id: "secondary", label: "Secondary", matches: /secondary/i },
  { id: "administration", label: "Administration", matches: /administration/i },
];

/** The audiences a chip stands for, or null when the value is one exact audience. */
function audiencesInGroup(guide: PublicConventionGuide, value: string): ReadonlySet<string> | null {
  const group = AUDIENCE_GROUPS.find((candidate) => `group:${candidate.id}` === value);
  if (!group) return null;
  return new Set(guide.audiences.filter((audience) => group.matches.test(audience.label)).map((audience) => audience.id));
}

/**
 * Tell the association what was looked for, once she has stopped typing.
 *
 * A schedule search runs on every keystroke and almost none of those are questions. Waiting
 * out a pause is what turns keystrokes into a question, and the pause is generous on purpose:
 * a teacher hunting a half-remembered title types slowly.
 *
 * A term that is only the beginning of the next one is never sent, so "gram" then "grammar"
 * counts as one search rather than inflating whatever she was actually after.
 *
 * Nothing about her goes with it. No sign-in, no registration, no identifier — the endpoint
 * records the question and returns nothing. And a failure is swallowed whole: a teacher must
 * never see anything go wrong because of what she typed into a search box.
 */
function useSearchLog(eventSlug: string, query: string, results: number): void {
  const lastSent = useRef<string | null>(null);
  useEffect(() => {
    const term = normalizeSearchTerm(query);
    if (!term) return;
    const timer = window.setTimeout(() => {
      if (supersedesSearchTerm(lastSent.current, term)) {
        // Same question, further along. Replace what we would have sent rather than send twice.
        lastSent.current = term;
        return;
      }
      if (lastSent.current === term) return;
      lastSent.current = term;
      void fetch(`/api/attendee/events/${encodeURIComponent(eventSlug)}/search-term`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ term, results }),
        keepalive: true,
      }).catch(() => undefined);
    }, 1_200);
    return () => window.clearTimeout(timer);
  }, [eventSlug, query, results]);
}

function Schedule({ guide, search, currentBlockId, nextBlockId, savedItemIds, updateSavedItemIds, schoolProposals }: { guide: PublicConventionGuide; search: string; currentBlockId: string | null; nextBlockId: string | null; savedItemIds: readonly string[]; updateSavedItemIds: (ids: readonly string[]) => void; schoolProposals?: ReturnType<typeof useSchoolProposals> }) {
  useDocumentTitle(`Schedule · ${guide.event.name}`);
  // The day the event is actually on, not the first day of it. A teacher who taps
  // Schedule on the Friday morning was landing on Thursday.
  const todayId = scheduleDayId(guide, currentBlockId, nextBlockId);
  const [filters, setFilters] = useState(() => scheduleFiltersFromSearch(search, todayId));
  useEffect(() => setFilters(scheduleFiltersFromSearch(search, todayId)), [search, todayId]);
  const searchable = useMemo(() => buildSearchableItems(guide), [guide]);
  const chipAudiences = audiencesInGroup(guide, filters.audience);
  // A search spans the convention; the day pills govern browsing.
  //
  // Searching and browsing are different acts. "Where is that grammar session" does not
  // care which day it is on, and a teacher who has to guess the day before she can find
  // the thing is being asked to know the answer before she asks the question. The
  // dropdown filters below are deliberately not like this: Audience, Room and Type refine
  // the day being looked at, and if they spanned days too the pills would silently stop
  // meaning anything the moment one was set.
  const searching = filters.query.trim().length > 0;
  const results = filterSchedule(searchable, {
    ...(searching ? {} : { dayId: filters.day }),
    query: filters.query,
    ...(filters.audience && !chipAudiences ? { audienceId: filters.audience } : {}),
    ...(filters.room ? { roomId: filters.room } : {}),
    ...(filters.kind ? { kind: filters.kind } : {}),
  });
  const withinChip = chipAudiences
    ? results.filter((item) => guide.items.find((candidate) => candidate.id === item.id)?.audienceIds.some((id) => chipAudiences.has(id)) ?? false)
    : results;
  const resultIds = new Set(withinChip.map((item) => item.id));
  useSearchLog(guide.event.slug, filters.query, withinChip.length);
  const savedIds = new Set(savedItemIds);
  const toggleSaved = (item: PublicScheduleItem) => updateSavedItemIds(toggleSavedItem(guide, savedItemIds, item));
  const day = guide.days.find((candidate) => candidate.id === filters.day) ?? guide.days[0];
  const dayBlocks = orderedBlocks(guide, day?.id ?? "");
  const states = blockStates(guide, day?.id ?? "", currentBlockId, nextBlockId);
  const blocks = dayBlocks.filter((block) => block.itemIds.some((id) => resultIds.has(id)));

  // One group per day that has something in it. Browsing produces exactly one; a search
  // produces as many as matched, in the convention's own order, each keeping its own
  // sense of what is running and what has finished.
  const groups = (searching ? guide.days : day ? [day] : [])
    .map((candidate) => ({
      day: candidate,
      blocks: orderedBlocks(guide, candidate.id).filter((block) => block.itemIds.some((id) => resultIds.has(id))),
      states: candidate.id === day?.id ? states : blockStates(guide, candidate.id, currentBlockId, nextBlockId),
    }))
    .filter((group) => group.blocks.length > 0);

  const update = (name: keyof typeof filters, value: string) => {
    const next = { ...filters, [name]: value };
    setFilters(next);
    replaceSearch(scheduleParams(next));
  };
  const clear = () => {
    // The block somebody arrived on is not a filter and clearing the filters must not
    // close it under them.
    const next = { day: filters.day, query: "", audience: "", room: "", kind: "", block: filters.block };
    setFilters(next);
    replaceSearch(scheduleParams(next));
  };
  const filtered = Boolean(filters.query || filters.audience || filters.room || filters.kind);
  // The chip is on screen and says so itself. The badge counts what is hidden behind the
  // icon, which is the only thing a number there can usefully tell somebody.
  const activeFilterCount = [audiencesInGroup(guide, filters.audience) ? "" : filters.audience, filters.room, filters.kind].filter(Boolean).length;

  // What has finished folds away, but only while the day still holds something
  // ahead. On a day that is wholly over — Thursday looked at on the Friday, or the
  // whole convention afterwards — hiding all of it would leave an empty page.
  const somethingAhead = blocks.some((block) => states.get(block.id) !== "past");
  const foldFinished = somethingAhead && !filtered;
  const finished = foldFinished ? blocks.filter((block) => states.get(block.id) === "past") : [];
  const remaining = foldFinished ? blocks.filter((block) => states.get(block.id) !== "past") : blocks;

  // A block opens on a tap. A search opens everything it matched, because a closed
  // block is a place a result can hide. The running block opens itself only while
  // its choice is still open — once she has saved something the row already says
  // where she is going, and ten alternatives are not what she needs in a corridor.
  const [searchOpen, setSearchOpen] = useState(() => Boolean(new URLSearchParams(search).get("q")));
  const [filterOpen, setFilterOpen] = useState(false);
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const selectedItem = guide.items.find((item) => item.id === selectedItemId && resultIds.has(item.id))
    ?? dayBlocks.flatMap((block) => block.itemIds).map((id) => guide.items.find((item) => item.id === id)).find((item) => item && savedIds.has(item.id))
    ?? guide.items.find((item) => item.id === withinChip[0]?.id)
    ?? null;
  const selectedDayId = selectedItem
    ? guide.blocks.find((candidate) => candidate.id === selectedItem.blockId)?.dayId ?? day?.id ?? ""
    : day?.id ?? "";
  const undecided = (blockId: string) => {
    const block = dayBlocks.find((candidate) => candidate.id === blockId);
    return Boolean(block) && block!.itemIds.length > 1 && !block!.itemIds.some((id) => savedIds.has(id));
  };
  // A search opens every block it matched, and it outranks a block somebody closed
  // by hand — otherwise the one session being searched for hides inside the block
  // that was collapsed ten minutes ago.
  // A block somebody asked for by name opens, and stays open until they close it.
  const isOpen = (blockId: string) => filtered
    || (toggled[blockId] ?? (blockId === filters.block || (blockId === currentBlockId && undecided(blockId))));
  const row = (block: PublicScheduleBlock, blockStatesForDay: ReturnType<typeof blockStates> = states) => (
    <ScheduleBlockRow
      key={block.id}
      guide={guide}
      block={block}
      resultIds={resultIds}
      savedIds={savedIds}
      onToggleSaved={toggleSaved}
      onSelectItem={setSelectedItemId}
      state={blockStatesForDay.get(block.id) ?? "ahead"}
      searching={filtered}
      proposedItemIds={schoolProposals?.proposedItemIds ?? new Set()}
      open={isOpen(block.id)}
      onToggle={() => setToggled((current) => ({ ...current, [block.id]: !isOpen(block.id) }))}
    />
  );

  if (filters.day === MY_SCHEDULE) {
    return (
      <div className="page schedule-page">
        <header className="schedule-head">
          <h1 data-page-heading tabIndex={-1}>Full schedule</h1>
        </header>
        <div className="schedule-day-bar">
          <div className="day-pills" role="group" aria-label="Convention day">
            {guide.days.map((candidate) => <button key={candidate.id} type="button" onClick={() => update("day", candidate.id)}>{dayPillLabel(candidate.label)}</button>)}
            <button type="button" className="active" aria-pressed="true">Mine</button>
          </div>
        </div>
        <MySchedule guide={guide} savedIds={savedIds} currentBlockId={currentBlockId} nextBlockId={nextBlockId} />
      </div>
    );
  }

  return (
    <div className="page schedule-page">
      <header className="schedule-head">
        <h1 data-page-heading tabIndex={-1}>Full schedule</h1>
        <div className="schedule-head-tools">
          <button type="button" className={`schedule-tool${searchOpen ? " is-on" : ""}`} aria-pressed={searchOpen} aria-label="Search the schedule" onClick={() => setSearchOpen((open) => !open)}><AppIcon name="search" /></button>
          <button type="button" className={`schedule-tool${filterOpen ? " is-on" : ""}`} aria-pressed={filterOpen} aria-label="Filter the schedule" onClick={() => setFilterOpen((open) => !open)}><AppIcon name="filters" />{activeFilterCount > 0 && <b>{activeFilterCount}</b>}</button>
        </div>
      </header>
      {schoolProposals?.proposalSet && <p className="school-suggestion-summary"><strong>{schoolProposals.proposalSet.organizationName} has suggested {schoolProposals.proposalSet.proposals.length} {schoolProposals.proposalSet.proposals.length === 1 ? "session" : "sessions"}.</strong> You decide what to add to your plan.</p>}
      <div className="schedule-planner-grid">
        <aside className="schedule-planner-tools" aria-label="Schedule planning tools">
          <div className="schedule-day-bar">
            <div className="day-pills" role="group" aria-label="Convention day">
              {guide.days.map((candidate) => <button key={candidate.id} type="button" className={filters.day === candidate.id ? "active" : ""} aria-pressed={filters.day === candidate.id} onClick={() => update("day", candidate.id)}>{dayPillLabel(candidate.label)}</button>)}
              {/* The third place is not a day. Thursday and Friday are for finding something;
                  this one is for knowing where to be next, which is a different question and
                  wants the whole convention rather than one page of it. */}
              <button type="button" className={filters.day === MY_SCHEDULE ? "active" : ""} aria-pressed={filters.day === MY_SCHEDULE} onClick={() => update("day", MY_SCHEDULE)}>Mine</button>
            </div>
          </div>
          {/* The audiences, promoted out of a dropdown. A teacher picking sessions is asking
              "what is for me", and it is the only filter that answers that question — room
              and type are for somebody looking for one particular thing, so they stay behind
              the icon. The row scrolls, because WVCEA publishes fifteen of these. */}
          <div className="audience-chips-bar" role="group" aria-label="Audience">
            <button type="button" className={filters.audience === "" ? "active" : ""} aria-pressed={filters.audience === ""} onClick={() => update("audience", "")}>All</button>
            {AUDIENCE_GROUPS.map((group) => {
              const value = `group:${group.id}`;
              return <button key={group.id} type="button" className={filters.audience === value ? "active" : ""} aria-pressed={filters.audience === value} onClick={() => update("audience", filters.audience === value ? "" : value)}>{group.label}</button>;
            })}
          </div>
          <label className={`search-field schedule-search${searchOpen ? "" : " desktop-planner-control"}`}><span className="visually-hidden">Search schedule</span><AppIcon name="search" /><input type="search" autoFocus={searchOpen} value={filters.query} onChange={(event) => update("query", event.target.value)} placeholder="Search sessions or presenters" /></label>
          <div className={`filter-grid${filterOpen ? "" : " desktop-planner-control"}`}>
            <Select label="Audience" value={audiencesInGroup(guide, filters.audience) ? "" : filters.audience} onChange={(value) => update("audience", value)} options={guide.audiences.map((value) => ({ value: value.id, label: value.label }))} />
            <Select label="Room" value={filters.room} onChange={(value) => update("room", value)} options={guide.rooms.map((value) => ({ value: value.id, label: roomLabel(value) }))} />
            <Select label="Type" value={filters.kind} onChange={(value) => update("kind", value)} options={[{ value: "instructional", label: "Seminars" }, { value: "general_session", label: "General sessions" }, { value: "meeting", label: "Meetings" }, { value: "special", label: "Convention activities" }]} />
          </div>
          {/* Only while searching or filtering, where the number answers "did that do
              anything". With nothing applied the count is noise: the day buttons above
              already say which day this is, and the list itself is the answer. */}
          <div className="results-line" aria-live="polite">{filtered && <><span><strong>{withinChip.length}</strong> {withinChip.length === 1 ? "activity" : "activities"} match</span><button type="button" className="clear-button" onClick={clear}>Clear</button></>}</div>
        </aside>
        <div className="schedule-planner-canvas">
          {groups.length === 0 ? (
            <section className="empty-state"><h2>No activities match.</h2><button type="button" onClick={clear}>Clear filters</button></section>
          ) : searching ? (
            // Grouped under the day, because a result on the wrong day is worse than no
            // result — a teacher who walks to Chapel on Thursday for a Friday seminar has
            // been actively misled. The heading is drawn even when only one day matched, so
            // the answer never depends on noticing that a second heading is absent.
            groups.map((group) => (
              <section className="schedule-day-group" key={group.day.id}>
                <h2 className="schedule-day-heading">{group.day.label}</h2>
                <div className="schedule-list">{group.blocks.map((block) => row(block, group.states))}</div>
              </section>
            ))
          ) : (
            <>
              {finished.length > 0 && (
                <details className="schedule-earlier">
                  <summary>Earlier today<span>{finished.length} finished</span></summary>
                  <div className="schedule-list">{finished.map((block) => row(block))}</div>
                </details>
              )}
              <div className="schedule-list">{remaining.map((block) => row(block))}</div>
            </>
          )}
        </div>
        <DesktopScheduleInspector guide={guide} dayId={selectedDayId} item={selectedItem} savedIds={savedIds} onToggleSaved={toggleSaved} currentBlockId={currentBlockId} nextBlockId={nextBlockId} schoolSuggested={Boolean(selectedItem && schoolProposals?.proposedItemIds.has(selectedItem.id))} />
      </div>
    </div>
  );
}

function DesktopScheduleInspector({ guide, dayId, item, savedIds, onToggleSaved, currentBlockId, nextBlockId, schoolSuggested }: {
  guide: PublicConventionGuide;
  dayId: string;
  item: PublicScheduleItem | null;
  savedIds: ReadonlySet<string>;
  onToggleSaved: (item: PublicScheduleItem) => void;
  currentBlockId: string | null;
  nextBlockId: string | null;
  schoolSuggested: boolean;
}) {
  const block = item ? guide.blocks.find((candidate) => candidate.id === item.blockId) : null;
  const room = item?.roomId ? guide.rooms.find((candidate) => candidate.id === item.roomId) : null;
  const presenters = item?.presenterIds.map((id) => guide.presenters.find((candidate) => candidate.id === id)?.displayName).filter(isString) ?? [];
  const audiences = item?.audienceIds.map((id) => guide.audiences.find((candidate) => candidate.id === id)?.label).filter(isString) ?? [];
  const choiceBlocks = orderedBlocks(guide, dayId).filter((candidate) => candidate.itemIds.length > 1);
  const choicesMade = choiceBlocks.filter((candidate) => candidate.itemIds.some((id) => savedIds.has(id))).length;
  const plan = dayTimeline(guide, savedIds, dayId, currentBlockId, nextBlockId);

  return (
    <aside className="schedule-planner-inspector" aria-label="Activity details and personal plan">
      <section className="planner-detail">
        <p className="planner-kicker">Activity detail</p>
        {item && block ? <>
          <h2>{item.title}</h2>
          <p className="planner-time">{formatTimeRange(block.startTime, block.endTime)}{room ? ` · ${roomLabel(room)}` : ""}</p>
          {presenters.length > 0 && <p className="planner-presenters">{presenters.join(", ")}</p>}
          {item.description && <p className="planner-description">{item.description}</p>}
          {audiences.length > 0 && <p className="planner-audiences"><strong>For:</strong> {audiences.join(", ")}</p>}
          {schoolSuggested && <p className="school-suggestion-note"><strong>Your school suggests this session.</strong><span>You can add it to your plan or choose another session.</span></p>}
          <div className="planner-detail-actions">
            <button type="button" className="primary-action" aria-pressed={savedIds.has(item.id)} onClick={() => onToggleSaved(item)}>{savedIds.has(item.id) ? "Remove from my plan" : "Add to my plan"}</button>
            <Link href={`/schedule/${encodeURIComponent(item.id)}`}>Open full Activity</Link>
          </div>
        </> : <p className="planner-empty">Choose an Activity to see its details here.</p>}
      </section>
      <section className="planner-plan">
        <header>
          <div><p className="planner-kicker">My plan</p><h2>{guide.days.find((candidate) => candidate.id === dayId)?.label ?? "Convention"}</h2></div>
          {choiceBlocks.length > 0 && <strong>{choicesMade}/{choiceBlocks.length}</strong>}
        </header>
        {choiceBlocks.length > 0 && <p className="planner-progress">{choicesMade === choiceBlocks.length ? "All choices made" : `${choiceBlocks.length - choicesMade} ${choiceBlocks.length - choicesMade === 1 ? "choice" : "choices"} left`}</p>}
        <ol>
          {plan.map((row) => <li key={row.key} className={row.awaitingChoice ? "needs-choice" : ""}><time dateTime={row.startTime}>{formatClock(row.startTime)}</time><Link href={row.href}><strong>{row.title}</strong><small>{row.detail}</small></Link></li>)}
        </ol>
      </section>
    </aside>
  );
}

/** Every schedule address carries its day. Leaving it off meant "whatever day it
    is now", which quietly moved a link somebody had followed on purpose. */
function scheduleParams(filters: { day: string; query: string; audience: string; room: string; kind: string; block?: string }): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.day) params.set("day", filters.day);
  if (filters.query) params.set("q", filters.query);
  if (filters.audience) params.set("audience", filters.audience);
  if (filters.room) params.set("room", filters.room);
  if (filters.kind) params.set("kind", filters.kind);
  // Kept in the address so a reload, or a link somebody sent, lands on the same block.
  if (filters.block) params.set("block", filters.block);
  return params;
}

function Select({ label, value, options, onChange }: { label: string; value: string; options: readonly { value: string; label: string }[]; onChange: (value: string) => void }) {
  return <label><span>{label}</span><select value={value} onChange={(event) => onChange(event.target.value)}><option value="">All</option>{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>;
}

/**
 * One block of the day. The day is its blocks, not its sixty-six sessions: a block
 * holding one fixed activity is a row that opens it, and a block holding a choice
 * is a row that opens the choice underneath. Thursday is seven rows rather than
 * forty-three cards.
 */
/** The third segment on the schedule bar. Not a day, so it is named rather than numbered. */
const MY_SCHEDULE = "mine";

/**
 * The itinerary: both days at once, one row a block, showing whatever has been chosen.
 *
 * It is drawn in full before anybody has chosen anything, because most of a convention
 * is fixed for everybody and an empty page would be a lie about that. A seminar row
 * says "choose from ten" until it can say a session's name instead, so the screen
 * becomes personal by being used rather than by demanding an account first.
 */
function MySchedule({ guide, savedIds, currentBlockId, nextBlockId }: {
  guide: PublicConventionGuide;
  savedIds: ReadonlySet<string>;
  currentBlockId: string | null;
  nextBlockId: string | null;
}) {
  // The day the convention is actually on leads, so somebody opening this on the Friday
  // is not reading Thursday first.
  const todayId = scheduleDayId(guide, currentBlockId, nextBlockId);
  const days = [...guide.days].sort((left, right) =>
    (left.id === todayId ? 0 : 1) - (right.id === todayId ? 0 : 1));
  return (
    <section className="my-schedule" aria-label="Your schedule">
      {days.map((day) => (
        <div key={day.id} className="my-schedule-day">
          <h2>{day.label}{day.id === todayId && <em>Today</em>}</h2>
          <ol className="day-rail">
            {dayTimeline(guide, savedIds, day.id, currentBlockId, nextBlockId).map((row) => (
              <li key={row.key} className={`rail-row is-${row.state}${row.awaitingChoice ? " is-open" : ""}${row.chosen ? " is-chosen" : ""}`}>
                <time dateTime={row.startTime}>{formatClock(row.startTime)}</time>
                <span className="rail-dot" aria-hidden="true" />
                <Link className="rail-body" href={row.href}>
                  {row.chosen && <em>Your selection</em>}
                  <strong>{row.title}</strong>
                  <small>{row.detail}</small>
                </Link>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </section>
  );
}

function ScheduleBlockRow({ guide, block, resultIds, savedIds, onToggleSaved, onSelectItem, state, searching, proposedItemIds, open, onToggle }: {
  guide: PublicConventionGuide;
  block: PublicScheduleBlock;
  resultIds: ReadonlySet<string>;
  savedIds: ReadonlySet<string>;
  onToggleSaved: (item: PublicScheduleItem) => void;
  onSelectItem: (itemId: string) => void;
  state: RowState;
  /** A search is on, so the session somebody looked for leads and the block names it. */
  searching: boolean;
  proposedItemIds: ReadonlySet<string>;
  open: boolean;
  onToggle: () => void;
}) {
  const items = block.itemIds.map((id) => guide.items.find((item) => item.id === id)).filter(isItem).filter((item) => resultIds.has(item.id));
  const day = guide.days.find((candidate) => candidate.id === block.dayId);
  const saved = items.filter((item) => savedIds.has(item.id));
  const only = items.length === 1 ? items[0] : undefined;
  const time = (
    <p className="block-time">
      <time dateTime={`${day?.date ?? ""}T${block.startTime}`}>{formatTimeRange(block.startTime, block.endTime)}</time>
      {state === "now" && <span className="now-marker">Happening now</span>}
    </p>
  );
  /* "10 sessions offered", as the drawing has it. It used to name the chosen session
     here, which ran to two lines and crowded the card — and now that the sessions nest
     inside the block, the one somebody picked is visible in place, tinted and marked,
     rather than described in a line above them. */
  const detail = state === "past" && saved.length === 0
    ? "No session saved"
    : `${items.length} ${items.length === 1 ? "session" : "sessions"} offered`;

  // A block with one activity in it has nothing to open. The row is the activity.
  if (only) {
    const sessionLeads = searching && only.title !== block.title;
    const lead = sessionLeads ? only.title : block.title;
    const under = sessionLeads
      ? [block.title, presenterOrRoom(guide, only)].filter(Boolean).join(" · ")
      // Lunch is called Lunch inside a block called Lunch, so saying it twice tells
      // nobody anything: the row says who it is for instead.
      : only.title === block.title ? presenterOrRoom(guide, only) ?? "Everyone" : only.title;
    return (
      <section id={`block-${block.id}`} className={`schedule-block is-${state}`} aria-labelledby={`heading-${block.id}`}>
        {time}
        <Link className="block-row" href={`/schedule/${encodeURIComponent(only.id)}`} onPointerEnter={() => onSelectItem(only.id)} onFocus={() => onSelectItem(only.id)}>
          <h2 id={`heading-${block.id}`}>{lead}</h2>
          <small>{under}</small>
        </Link>
      </section>
    );
  }

  return (
    <section id={`block-${block.id}`} className={`schedule-block is-${state}${open ? " is-open" : ""}`} aria-labelledby={`heading-${block.id}`}>
      {time}
      <button type="button" className="block-row" aria-expanded={open} aria-controls={`sessions-${block.id}`} onClick={onToggle}>
        <h2 id={`heading-${block.id}`}>{block.title}</h2>
        <small>{detail}</small>
        <span className="block-chevron" aria-hidden="true">›</span>
      </button>
      {/* Words, not a button: this screen is for glancing, and the button is on the session
          page one tap away. Only while the block is running, and only for seminars. */}
      {state === "now" && items.some((item) => item.kind === "instructional") && (
        <p className="schedule-check-in">Scan the QR code to check in</p>
      )}
      <div className="schedule-items" id={`sessions-${block.id}`} hidden={!open}>
        {items.map((item) => <ScheduleItemLink key={item.id} guide={guide} item={item} saved={savedIds.has(item.id)} schoolSuggested={proposedItemIds.has(item.id)} onToggleSaved={() => onToggleSaved(item)} onSelect={() => onSelectItem(item.id)} />)}
      </div>
    </section>
  );
}

function presenterOrRoom(guide: PublicConventionGuide, item: PublicScheduleItem): string | null {
  const names = item.presenterIds.map((id) => guide.presenters.find((candidate) => candidate.id === id)?.displayName).filter(isString);
  if (names.length > 0) return names.join(", ");
  return item.roomId ? guide.rooms.find((candidate) => candidate.id === item.roomId)?.code ?? null : null;
}

/* The card is a plain box, not one big link, because the bookmark inside it is a button
   of its own and an anchor cannot hold one. The title link stretches an invisible layer
   across the whole card instead, so every bit of white space opens the session. The
   bookmark sits above that layer and saves without opening anything, which is what
   choosing one of ten seminars needs: ten taps rather than ten trips into a page and
   back out of it.

   The row carries the title and one quiet line of presenter and room. It used to also
   carry a kind kicker and two pill links into a filtered schedule; ten rows of that is a
   wall. Nothing is lost — the audience is on the session's own page, and the filters at
   the top of the schedule reach the same lists. */
function ScheduleItemLink({ guide, item, saved = false, schoolSuggested = false, onToggleSaved, onSelect, hidePresenterId }: { guide: PublicConventionGuide; item: PublicScheduleItem; saved?: boolean; schoolSuggested?: boolean; onToggleSaved: () => void; onSelect?: () => void; /** The presenter whose own page this row is on: their name under it says nothing. */ hidePresenterId?: string }) {
  const room = item.roomId ? guide.rooms.find((candidate) => candidate.id === item.roomId) : undefined;
  const presenters = item.presenterIds.filter((id) => id !== hidePresenterId).map((id) => guide.presenters.find((candidate) => candidate.id === id)?.displayName).filter(isString);
  // Inside an open seminar block every row is a seminar, so naming it ten times says
  // nothing. Anything else keeps its name, because a presenter's page mixes the kinds.
  const before = [item.kind === "instructional" ? null : itemLabel(item), presenters.join(", ") || null].filter((value): value is string => typeof value === "string");
  return (
    <div className={`schedule-item${saved ? " saved" : ""}`}>
      <Link className="item-title" href={`/schedule/${encodeURIComponent(item.id)}`} onPointerEnter={onSelect} onFocus={onSelect}><strong>{item.title}</strong></Link>
      {/* Two colours, as the drawing has them: who is speaking in grey, where it is in
          the accent. A room is the thing somebody scans a list for. */}
      {(before.length > 0 || room) && <span className="item-line">{before.join(" · ")}{before.length > 0 && room ? " · " : ""}{room && <b>{roomLabel(room)}</b>}</span>}
      {schoolSuggested && <span className="school-suggestion-label">Your school suggests this session</span>}
      <button
        type="button"
        className="item-save"
        aria-pressed={saved}
        aria-label={saved ? `Remove ${item.title} from your schedule` : `Save ${item.title} to your schedule`}
        onClick={onToggleSaved}
      >
        <AppIcon name="bookmark" />
      </button>
    </div>
  );
}

/**
 * Who is presenting, with their face when there is one.
 *
 * The photograph WVCEA uploads was reaching the presenter directory and the presenter's
 * own page, and nowhere else — this row drew initials whether or not a photograph
 * existed. A teacher opens the session, not the directory, so the one place the picture
 * was certain to be missed was the only place most people look.
 *
 * The block appeared twice, once inside the details disclosure and once on the plain
 * page, character for character. Two copies of a row is how the first one came to be
 * wrong on its own.
 */
function PresentedBy({ presenters }: { presenters: readonly PublicConventionGuide["presenters"][number][] }) {
  if (presenters.length === 0) return null;
  return (
    <section className="detail-section">
      <h2>Presented by</h2>
      {presenters.map((presenter) => (
        <Link className="presenter-line" href={`/more/presenters/${encodeURIComponent(presenter.id)}`} key={presenter.id}>
          {presenter.headshotUrl
            ? <img className="person-headshot" src={presenter.headshotUrl} alt="" />
            : <span className="person-initials" aria-hidden="true">{initials(presenter.displayName)}</span>}
          <span><strong>{presenter.displayName}</strong>{presenter.credentials && <small>{presenter.credentials}</small>}</span>
          <b aria-hidden="true">›</b>
        </Link>
      ))}
    </section>
  );
}

function SessionDetail({ guide, itemId, search, currentBlockId, nextBlockId, savedItemIds, updateSavedItemIds, privateNotes, accountState, sessionResources, schoolProposals }: { guide: PublicConventionGuide; itemId: string; search: string; currentBlockId: string | null; nextBlockId: string | null; savedItemIds: readonly string[]; updateSavedItemIds: (ids: readonly string[]) => void; privateNotes: ReturnType<typeof usePrivateNotes>; accountState: AttendeeAccountState; sessionResources: ReturnType<typeof useSessionResources>; schoolProposals: ReturnType<typeof useSchoolProposals> }) {
  const item = guide.items.find((candidate) => candidate.id === itemId);
  useDocumentTitle(item ? `${item.title} · ${guide.event.name}` : `Page not found · ${guide.event.name}`);
  if (!item) return <NotFound />;
  const block = guide.blocks.find((candidate) => candidate.id === item.blockId);
  const day = guide.days.find((candidate) => candidate.id === block?.dayId);
  const room = item.roomId ? guide.rooms.find((candidate) => candidate.id === item.roomId) : undefined;
  const presenters = item.presenterIds.map((id) => guide.presenters.find((candidate) => candidate.id === id)).filter(isPresenter);
  const audiences = item.audienceIds.map((id) => guide.audiences.find((candidate) => candidate.id === id)?.label).filter(isString);
  const resources = resourcesForItem(sessionResources, item.id);
  const note = privateNotes.notes[item.id];
  const hasNote = Boolean(note && (note.values.body.trim() || note.values.markers.some((marker) => marker.text.trim())));
  const timing = block ? blockStates(guide, block.dayId, currentBlockId, nextBlockId).get(block.id) ?? "ahead" : "ahead";
  const workspaceMode = sessionWorkspaceMode(timing);
  const saved = savedItemIds.includes(item.id);
  const conflict = savedItemIds.map((id) => guide.items.find((candidate) => candidate.id === id)).find((candidate) => candidate && candidate.blockId === item.blockId && candidate.id !== item.id);
  const toggleSaved = () => updateSavedItemIds(toggleSavedItem(guide, savedItemIds, item));
  const schoolProposal = schoolProposals.proposalSet?.proposals.find((proposal) => proposal.itemId === item.id) ?? null;
  const sessionActions = <>
    {schoolProposal && <p className="school-suggestion-note"><strong>Your school suggests this session for {schoolProposal.blockTitle}.</strong><span>You can add it to your plan or choose another session.</span></p>}
    {conflict && !saved && <p className="agenda-conflict" role="status">Saving this replaces the session you already chose at this time: {conflict.title}</p>}
    <div className="detail-actions">
      <button type="button" className={`primary-action${saved ? " remove" : ""}`} onClick={toggleSaved}>{saved ? `Remove from ${personalWorkspaceName()}` : conflict ? "Replace saved session" : `Add to ${personalWorkspaceName()}`}</button>
      {room && <Link className="secondary-action" href={`/map?room=${encodeURIComponent(room.code)}&from=/schedule/${encodeURIComponent(item.id)}`}><AppIcon name="map" /><span>Show {roomLabel(room)} on the map</span><b aria-hidden="true">›</b></Link>}
    </div>
  </>;
  /* Every fact in the green, and only words underneath it.
     A teacher checking a room in a corridor should never scroll for it, so the box
     carries the day, the hour, the presenter, the room and the audience — and the room
     is a link into the map, because knowing where it is and going there are the same
     errand. What is left below is the description, which is what somebody reads when
     they are deciding rather than walking. */
  const hero = (
    <header className="detail-hero">
      {/* Seminars only: they are the sessions with a QR code in the room and a contact hour
          behind it. On a general session the corner would point at a code that does not exist. */}
      {item.kind === "instructional" && <CheckInButton eventSlug={guide.event.slug} itemId={item.id} />}
      <p className="detail-label">{itemLabel(item)}{day ? ` · ${day.label.split(",")[0]}` : ""}</p>
      <h1 data-page-heading tabIndex={-1}>{item.title}</h1>
      {block && <p className="detail-time">{formatTimeRange(block.startTime, block.endTime)}</p>}
      {presenters.length > 0 && <p className="detail-presenters">{presenters.map((presenter) => presenter.displayName).join(", ")}</p>}
      <div className="detail-hero-facts">
        {room && <Link className="hero-chip" href={`/map?room=${encodeURIComponent(room.code)}&from=/schedule/${encodeURIComponent(item.id)}`}>{roomLabel(room)} <b aria-hidden="true">›</b></Link>}
        {audiences.map((audience) => <span className="hero-chip" key={audience}>{audience}</span>)}
      </div>
    </header>
  );
  /** Before the hour. The workspace still has all three modes; this only orders the page. */
  const before = workspaceMode === "before";

  /**
   * Who gets somewhere to write and something to record with.
   *
   * This was `instructional` alone, which is the test for continuing-education credit —
   * one flag doing two jobs. Credit and note-taking are different questions: WVCEA grants
   * no credit for the preaching, and a teacher still writes things down in it. The three
   * general sessions had no notes, no materials and no record button, and the recording
   * layer had been built expecting them all along: `sessionRecordingAllowed` has a branch
   * for items with nobody to ask, and 0091's comment names a general session as the thing
   * the old override table still governs.
   *
   * Meetings and the special activities get neither, deliberately. A board meeting
   * recorded by whoever wandered in is a governance question rather than a product one,
   * and recording lunch or the exhibit hall captures corridor talk from people who agreed
   * to nothing.
   */
  const notes = item.kind === "instructional" || item.kind === "general_session"
    ? <SessionWorkspace item={item} eventSlug={guide.event.slug} mode={workspaceMode} search={search} resourceState={sessionResources} resources={resources} hasNote={hasNote} privateNotes={privateNotes} signedIn={accountState.status === "signed-in"} saved={savedItemIds.includes(item.id)} />
    : null;

  /**
   * The day, the time, the room and the audience, kept folded.
   *
   * Every one of them is already in the card at the top of this page, in larger type, and
   * printing them again immediately underneath was the first thing on the screen — a list
   * telling a teacher what she had just read. The organization is the only line here she
   * cannot see above, which is why the disclosure stays rather than the whole thing going.
   */
  const specifics = (
    <div className="session-details-body">
      <dl className="detail-facts">
        <div><dt>When</dt><dd><time dateTime={`${day?.date ?? ""}T${block?.startTime ?? ""}`}>{day?.label}{block ? ` · ${formatTimeRange(block.startTime, block.endTime)}` : ""}</time></dd></div>
        <div><dt>Room</dt><dd>{room ? <>{roomLabel(room)}{roomLabel(room) === room.name ? null : <span>{room.name}</span>}</> : "Location has not been supplied."}</dd></div>
        {item.organization && <div><dt>Organization</dt><dd>{item.organization}</dd></div>}
      </dl>
      {audiences.length > 0 && <div className="audience-chips" aria-label="Audience">{audiences.map((audience) => <span key={audience}>{audience}</span>)}</div>}
    </div>
  );

  /**
   * What she cannot get from the card: what the session is actually about.
   *
   * Drawn only when there is something to say. It used to print "No description was
   * supplied" — a sentence about WVCEA's data entry, in the place a teacher was looking
   * for the session, which reads as an application that has lost something. Silence is
   * better: the card above already carries the title, the hour, the room and who is
   * leading it, and that is a complete answer for a session nobody wrote a blurb for.
   *
   * Never on a general session. Those are the convention's preaching, titled and
   * attributed on the card, and a heading called "About" over a sermon is
   * an empty frame asking to be filled.
   */
  const about = item.kind !== "general_session" && item.description
    ? (
      <div className="session-details-body">
        <section className="detail-section"><h2>About</h2><p className="description">{item.description}</p></section>
      </div>
    )
    : null;

  return (
    <article className="detail-page">
      <Link className="back-link" href={`/schedule${day ? `?day=${encodeURIComponent(day.id)}` : ""}`}>← Back to schedule</Link>
      {hero}
      {/* The page reorders itself around the clock, because the question changes.
          Before the hour, a teacher is deciding: what is this, is it worth my next
          forty-five minutes. So the description sits open under the card and her notes
          wait, folded, for the thought she has now. Once it starts she is in the room and
          has stopped deciding, so the notes take the screen and the description folds
          away with everything else.

          What does not move is who is presenting. It sits at the foot of the page in both
          states, because a teacher looking up the presenter is doing it at either hour and
          should not have to learn two places to find her. */}
      {before ? <>
        {about}
        {sessionActions}
        {notes && <details className="session-notes-disclosure">
          <summary>Notes</summary>
          {notes}
        </details>}
        <details className="session-details-disclosure">
          <summary>Session details</summary>
          {specifics}
        </details>
      </> : <>
        {notes}
        {sessionActions}
        <details className="session-details-disclosure">
          <summary>Session details</summary>
          {specifics}
          {about}
        </details>
      </>}
      <div className="session-details-body"><PresentedBy presenters={presenters} /></div>
      <SessionSurveyEntry eventSlug={guide.event.slug} itemId={item.id} signedIn={accountState.status === "signed-in"} />
    </article>
  );
}

function workspaceHeading(mode: SessionWorkspaceMode): string {
  if (mode === "during") return "During this session";
  if (mode === "after") return "After this session";
  return "Before this session";
}

function workspaceDetail(mode: SessionWorkspaceMode): string {
  if (mode === "during") return "Your notes save as you type. Open a file without losing your place.";
  if (mode === "after") return "Review what you captured, then turn it into a Learning Record.";
  // Written while she is choosing, which is when she knows why she chose it.
  return "Note what you want to ask or watch for. It will be here when the session starts.";
}

type SessionWorkspaceView = "notes" | "materials";

function SessionWorkspace({ item, eventSlug, mode, search, resourceState, resources, hasNote, privateNotes, signedIn, saved }: { item: PublicScheduleItem; eventSlug: string; mode: SessionWorkspaceMode; search: string; resourceState: ReturnType<typeof useSessionResources>; resources: readonly ReturnType<typeof resourcesForItem>[number][]; hasNote: boolean; privateNotes: ReturnType<typeof usePrivateNotes>; signedIn: boolean; /** Whether she chose this session, which decides whether it is already hers. */ saved: boolean }) {
  const requestedView = new URLSearchParams(search).get("view");
  const initialView: SessionWorkspaceView = requestedView === "materials" ? "materials" : "notes";
  const [view, setView] = useState<SessionWorkspaceView>(initialView);
  const followup = useSessionFollowup(eventSlug, item.id, mode === "after" && signedIn);
  useEffect(() => {
    setView(initialView);
    if (requestedView === "notes" || requestedView === "materials") {
      window.requestAnimationFrame(() => document.getElementById("during-session")?.scrollIntoView({ block: "start" }));
    }
  }, [initialView, item.id, requestedView]);
  const chooseView = (next: SessionWorkspaceView) => {
    setView(next);
    const params = new URLSearchParams(window.location.search);
    params.set("view", next);
    replaceSearch(params);
  };
  const moveView = (event: KeyboardEvent<HTMLButtonElement>, next: SessionWorkspaceView) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    chooseView(next);
    document.getElementById(next === "notes" ? "session-notes-tab" : "session-materials-tab")?.focus();
  };
  return (
    <section className="session-workspace" id="during-session" aria-labelledby="session-workspace-title">
      <header className="session-workspace-heading">
        <h2 id="session-workspace-title">{workspaceHeading(mode)}</h2>
        <p>{workspaceDetail(mode)}</p>
      </header>
      {/* Only where there are hours to claim. The workspace now opens on the general
          sessions too, and WVCEA grants no credit for the preaching — so asking a teacher
          to finish a record that cannot exist would be inventing an errand. */}
      {mode === "after" && earnsContactHours(item.kind) && <SessionFollowup itemId={item.id} signedIn={signedIn} hasNote={hasNote} state={followup.state} retry={followup.retry} />}
      {/* Above the tabs, because a teacher who came here to record should not have to
          find the right tab first. It draws nothing on the website and nothing when the
          presenter declined. */}
      <RecordSessionControl itemId={item.id} title={item.title} allowed={item.allowsRecording === true} signedIn={signedIn} />
      {/* Only after it is over, and only for a seminar she did not choose. Offering this
          while the block is still running would be inviting her to give up on the room she
          is sitting in. It draws nothing until WVCEA opens the door. */}
      {mode === "after" && (
        <StudySessionControl
          eventSlug={eventSlug}
          itemId={item.id}
          signedIn={signedIn}
          saved={saved}
          creditable={earnsContactHours(item.kind)}
        />
      )}
      <div className="session-workspace-tabs" role="tablist" aria-label={workspaceHeading(mode)}>
        <button id="session-notes-tab" type="button" role="tab" tabIndex={view === "notes" ? 0 : -1} aria-selected={view === "notes"} aria-controls="session-notes-panel" onClick={() => chooseView("notes")} onKeyDown={(event) => moveView(event, "materials")}>My notes</button>
        <button id="session-materials-tab" type="button" role="tab" tabIndex={view === "materials" ? 0 : -1} aria-selected={view === "materials"} aria-controls="session-materials-panel" onClick={() => chooseView("materials")} onKeyDown={(event) => moveView(event, "notes")}>Materials {resourceState.status === "ready" && <b>{resources.length}</b>}</button>
      </div>
      <div id="session-notes-panel" className="session-workspace-panel" role="tabpanel" aria-labelledby="session-notes-tab" hidden={view !== "notes"}>
        <SessionNotes itemId={item.id} title={item.title} privateNotes={privateNotes} signedIn={signedIn} />
      </div>
      <div id="session-materials-panel" className="session-workspace-panel" role="tabpanel" aria-labelledby="session-materials-tab" hidden={view !== "materials"}>
        <SessionMaterials resourceState={resourceState} resources={resources} signedIn={signedIn} eventSlug={eventSlug} embedded />
      </div>
    </section>
  );
}

type SessionFollowupLoadState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "unavailable" }
  | { status: "ready"; response: AttendeeLearningRecordResponse };

function useSessionFollowup(eventSlug: string, itemId: string, enabled: boolean) {
  const [state, setState] = useState<SessionFollowupLoadState>({ status: "idle" });
  const [attempt, setAttempt] = useState(0);
  const retry = () => { setState({ status: "loading" }); setAttempt((value) => value + 1); };
  useEffect(() => {
    if (!enabled) { setState({ status: "idle" }); return; }
    const controller = new AbortController();
    setState({ status: "loading" });
    void fetch(`/api/attendee/events/${encodeURIComponent(eventSlug)}/learning-records/${encodeURIComponent(itemId)}`, { credentials: "same-origin", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error();
        setState({ status: "ready", response: await response.json() as AttendeeLearningRecordResponse });
      })
      .catch(() => { if (!controller.signal.aborted) setState({ status: "unavailable" }); });
    return () => controller.abort();
  }, [attempt, enabled, eventSlug, itemId]);
  return { state, retry };
}

function SessionFollowup({ itemId, signedIn, hasNote, state, retry }: { itemId: string; signedIn: boolean; hasNote: boolean; state: SessionFollowupLoadState; retry: () => void }) {
  if (!signedIn) {
    return <Link className="session-learning-record-link" href="/account"><span><strong>Sign in to finish my Learning Record</strong><small>Your notes on this device will still be here.</small></span><b aria-hidden="true">›</b></Link>;
  }
  if (state.status === "loading" || state.status === "idle") {
    return <div className="session-followup-state" aria-live="polite"><span className="session-followup-spinner" aria-hidden="true" /><span><strong>Checking your Learning Record…</strong><small>Your notes remain available below.</small></span></div>;
  }
  if (state.status === "unavailable") {
    return <div className="session-followup-state error" role="status"><span><strong>We couldn’t check your Learning Record.</strong><small>Your notes are safe. Try again when your connection returns.</small></span><button type="button" onClick={retry}>Check again</button></div>;
  }
  const kind: SessionFollowupKind = sessionFollowupKind(state.response, hasNote);
  if (kind === "waiting") {
    return <div className="session-followup-state"><span><strong>Nothing has reached your Learning Record yet.</strong><small>If you made a recording, it may still be on your phone.</small></span><button type="button" onClick={retry}>Check again</button></div>;
  }
  const title = kind === "saved" ? "Open my Learning Record" : kind === "writing" ? "My first version is being written" : kind === "failed" ? "My first version needs another try" : "Create my Learning Record";
  const detail = kind === "saved" ? "Your saved record is ready." : kind === "writing" ? "You can open the page or come back later." : kind === "failed" ? "Your notes and session material are safe." : state.response.evidence.sources.some((source) => source.kind === "recording_transcript") ? "Your recording transcript is ready to use." : "Use your notes and the session material available.";
  return <Link className="session-learning-record-link" href={`/my-wvcea/learning/${encodeURIComponent(itemId)}`}><span><strong>{title}</strong><small>{detail}</small></span><b aria-hidden="true">›</b></Link>;
}

function SessionMaterials({ resourceState, resources, signedIn, eventSlug, embedded = false }: { resourceState: ReturnType<typeof useSessionResources>; resources: readonly ReturnType<typeof resourcesForItem>[number][]; signedIn: boolean; eventSlug: string; embedded?: boolean }) {
  if (resourceState.status === "ready" && resources.length > 0) return <SessionResources resources={resources} signedIn={signedIn} eventSlug={eventSlug} embedded={embedded} />;
  const title = resourceState.status === "loading" ? "Checking for presenter files…" : resourceState.status === "unavailable" ? "Presenter files could not be loaded." : "No presenter files yet";
  // Nothing under "No presenter files yet": an empty list saying files would appear here
  // if there were any tells a teacher what the emptiness already told them.
  const detail = resourceState.status === "loading" ? "This should take only a moment." : resourceState.status === "unavailable" ? "Your notes still work. Try the files again when your connection returns." : "";
  return <section className={`session-material-state${embedded ? " embedded" : ""}`} aria-live="polite">
    {!embedded && <h2>Materials</h2>}
    <div className={resourceState.status}>
      <span aria-hidden="true"><AppIcon name="document" /></span>
      <div><h3>{title}</h3>{detail && <p>{detail}</p>}</div>
      {resourceState.status === "unavailable" && <button type="button" onClick={resourceState.retry}>Try again</button>}
    </div>
  </section>;
}

/**
 * Her whole convention, as a file she keeps.
 *
 * Until now her notes lived inside this application and nowhere else, for as long as
 * somebody kept paying for it — and they are also the evidence behind her
 * continuing-education credit. Material that belongs to her and that she cannot take out
 * is material she does not really have.
 *
 * One document for the convention rather than one per session, because that is what a
 * person wants at the end of two days and what professional-development paperwork already
 * looks like. Without the transcript: a verbatim record is somebody else's words.
 */
function ExportNotesRow({ guide, privateNotes, attendeeName }: {
  guide: PublicConventionGuide;
  privateNotes: ReturnType<typeof usePrivateNotes>;
  attendeeName: string | null;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The same test the learning records use, so the door appears exactly when there is
  // something behind it.
  const notedItems = guide.items.filter((item) => {
    const note = privateNotes.notes[item.id];
    return Boolean(note && (note.values.body.trim() || note.values.markers.length > 0 || note.conflict));
  });
  if (!notedItems.length) return null;

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const { buildNoteExportPdf, noteExportFilename } = await import("./note-export-pdf");
      const blocks = new Map(guide.blocks.map((block) => [block.id, block]));
      const days = new Map(guide.days.map((day) => [day.id, day]));
      const rooms = new Map(guide.rooms.map((room) => [room.id, room]));
      const presenters = new Map(guide.presenters.map((presenter) => [presenter.id, presenter]));

      const exported = {
        eventName: guide.event.name,
        attendeeName,
        conventionDates: formatEventDates(guide.event.startDate, guide.event.endDate),
        notes: notedItems.map((item) => {
          const note = privateNotes.notes[item.id];
          const block = blocks.get(item.blockId);
          const day = block ? days.get(block.dayId) : undefined;
          return {
            title: item.title,
            presenters: item.presenterIds
              .map((presenterId) => presenters.get(presenterId)?.displayName)
              .filter((name): name is string => Boolean(name)),
            room: item.roomId ? rooms.get(item.roomId)?.code ?? null : null,
            // The day already carries a label written for a reader, so it is used rather
            // than a second date format invented here.
            when: [day?.label ?? null, block ? formatTimeRange(block.startTime, block.endTime) : null]
              .filter(Boolean).join(" · "),
            document: note?.values.document ? sanitizeNoteDocument(note.values.document) : null,
            body: note?.values.body ?? "",
            markers: note?.values.markers ?? [],
          };
        }),
      };

      const blob = await buildNoteExportPdf(exported);
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = noteExportFilename(exported);
      link.click();
      // Released on the next tick rather than immediately, because revoking it before the
      // browser has started the download cancels the download.
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      setError("Your notes could not be prepared. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" disabled={busy} onClick={() => void download()}>
        <span className="action-icon"><AppIcon name="document" /></span>
        <span><strong>{busy ? "Preparing your notes…" : "Download my notes"}</strong><small>{notedItems.length} {notedItems.length === 1 ? "session" : "sessions"}, as one file</small></span>
        <b aria-hidden="true">›</b>
      </button>
      {error && <p className="note-storage-error" role="alert">{error}</p>}
    </>
  );
}

function SessionNotes({ itemId, title, privateNotes, signedIn }: { itemId: string; title: string; privateNotes: ReturnType<typeof usePrivateNotes>; signedIn: boolean }) {
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const note = privateNotes.notes[itemId];
  const values = note?.values ?? { body: "", markers: [] };
  const status = privateNotes.statusFor(itemId);
  const statusText = status === "syncing" ? "Syncing…" : status === "synced" ? "Synced privately" : status === "offline" ? "Offline · saved on this device" : status === "conflict" ? "Two versions need your review" : status === "unavailable" ? "Device storage unavailable" : signedIn ? "Saves automatically" : "Saved on this device";
  const resolve = (action: "keep_current" | "use_incoming") => {
    setRecoveryError(null);
    void privateNotes.resolveConflict(itemId, action).catch((reason: unknown) => setRecoveryError(reason instanceof Error ? reason.message : "The note versions could not be resolved."));
  };
  return (
    <section className="session-notes" aria-labelledby={`notes-${itemId}`}>
      <header className="session-notes-heading">
        <h3 id={`notes-${itemId}`}>My private notes</h3>
        <span className={`note-save-state ${status}`} aria-live="polite">{statusText}</span>
      </header>
      {privateNotes.storageError && <p className="note-storage-error" role="alert">{privateNotes.storageError}</p>}
      {!privateNotes.storageError && privateNotes.syncError && <p className="note-sync-message" role="status">{privateNotes.syncError}</p>}
      {/* The document is sanitised on the way in as well as out: it arrives from storage
          or from another device, and neither is a place to start trusting one. */}
      <NoteEditor
        document={values.document ? sanitizeNoteDocument(values.document) : null}
        body={values.body}
        disabled={status === "unavailable"}
        label={`Notes for ${title}`}
        onChange={(next) => privateNotes.updateDocument(itemId, next)}
      />
      <div className="note-marker-toolbar" aria-label="Add a note marker">
        <span>Mark something to revisit</span>
        <button type="button" disabled={status === "unavailable"} onClick={() => privateNotes.addMarker(itemId, "key_idea")}>+ Key idea</button>
        <button type="button" disabled={status === "unavailable"} onClick={() => privateNotes.addMarker(itemId, "question")}>+ Question</button>
        <button type="button" disabled={status === "unavailable"} onClick={() => privateNotes.addMarker(itemId, "next_action")}>+ Next action</button>
      </div>
      {values.markers.length > 0 && <div className="note-markers">{values.markers.map((marker) => (
        <label className={`note-marker ${marker.kind}`} key={marker.id}>
          <span>{markerLabel(marker.kind)}</span>
          <input value={marker.text} disabled={status === "unavailable"} onChange={(event) => privateNotes.updateMarker(itemId, marker.id, event.target.value)} placeholder={`${markerLabel(marker.kind)}…`} maxLength={2000} />
          <button type="button" disabled={status === "unavailable"} onClick={() => privateNotes.removeMarker(itemId, marker.id)} aria-label={`Remove ${markerLabel(marker.kind).toLowerCase()}`}>×</button>
        </label>
      ))}</div>}
      {note?.conflict && <section className="note-recovery" aria-labelledby={`recovery-${itemId}`}>
        <div><p>Nothing was lost</p><h3 id={`recovery-${itemId}`}>This note changed on another device.</h3><p>Review both copies, then choose which one to keep.</p></div>
        <div className="note-version-grid">
          <article><strong>This device</strong><p>{note.values.body || "No paragraph notes"}</p><small>{note.values.markers.length} marked items</small><button type="button" onClick={() => resolve("use_incoming")}>Keep this device copy</button></article>
          <article><strong>Synced copy</strong><p>{note.conflict.current.values.body || "No paragraph notes"}</p><small>{note.conflict.current.values.markers.length} marked items</small><button type="button" onClick={() => resolve("keep_current")}>Use synced copy</button></article>
        </div>
        {recoveryError && <p className="note-storage-error" role="alert">{recoveryError}</p>}
      </section>}
      <p className="note-privacy"><span aria-hidden="true">●</span>Private to your account. It is not shown to other attendees, presenters, or your school.</p>
    </section>
  );
}

/**
 * My WVCEA: a hallway, not a page.
 *
 * It used to be two ideas stacked on each other — two rows that opened somewhere, over
 * three tabs that swapped content in place — so a teacher had to tap to find out which
 * kind of thing they were looking at. Every item is now a door.
 *
 * Continuing education leads, against the order of a convention and deliberately. It is
 * the reason attending counts for anything a year later, and the rest of this page is
 * conveniences beside it.
 *
 * My schedule is not here. Home's foot opens it and the Schedule tab holds it, and a
 * third route to the same list is a row that teaches somebody nothing.
 */
function MyConvention({ guide, mode, savedItemIds, accountState, syncStatus, privateNotes, resourceState, unreadUpdates }: { guide: PublicConventionGuide; mode: EventMode; savedItemIds: readonly string[]; accountState: AttendeeAccountState; syncStatus: "device" | "syncing" | "synced" | "offline"; privateNotes: ReturnType<typeof usePrivateNotes>; resourceState: ReturnType<typeof useSessionResources>; unreadUpdates: number }) {
  const recordMode = mode === "follow-through" || mode === "archive";
  const heading = myWvceaHeading();
  useDocumentTitle(`${heading} · ${guide.event.name}`);
  const savedSet = new Set(savedItemIds);
  const savedItems = guide.items.filter((item) => savedSet.has(item.id));
  const notedItems = guide.items.filter((item) => {
    const note = privateNotes.notes[item.id];
    return Boolean(note && (note.values.body.trim() || note.values.markers.length > 0 || note.conflict));
  });
  const resourceCount = resourceState.status === "ready" ? resourceState.resources.length : 0;
  const reviewed = useConferenceReviewSummary(accountState, guide.event.slug);
  const reviewedIds = new Set(reviewed?.evidence.sources.map((source) => source.itemId) ?? []);
  return (
    <div className="page personal-page">
      {recordMode ? <section className="event-record-hero"><p>{mode === "archive" ? "Event history" : "After the convention"}</p><h1 data-page-heading tabIndex={-1}>{guide.event.name}</h1><span>{formatEventDates(guide.event.startDate, guide.event.endDate)} · {guide.event.venueName}</span><div><strong>{savedItems.length}</strong><small>saved sessions</small><strong>{notedItems.length}</strong><small>session notes</small><strong>{reviewedIds.size}</strong><small>finished records</small></div></section>
        : <PageIntro title={heading} />}
      {accountState.status === "signed-in" ? (
        <section className="personal-account-state connected" aria-label="Account synchronization status"><span aria-hidden="true">✓</span><div><strong>Connected as {accountState.session.displayName}</strong><p>{syncStatus === "syncing" ? "Synchronizing your latest choices…" : syncStatus === "offline" ? "Offline changes will sync when you reconnect." : "Your schedule is available on your signed-in devices."}</p></div><Link href="/account">Account</Link></section>
      ) : (
        <section className="personal-account-state"><span aria-hidden="true">↗</span><div><strong>Keep this schedule across devices</strong><p>Your choices already work on this device. Sign in with your registration email to connect them.</p></div><Link href="/account">Connect</Link></section>
      )}
      <nav className="menu-list" aria-label={heading}>
        <ActionRow href="/my-wvcea/credit" icon="document" title="Continuing education credit" detail="" />
        <ActionRow href="/updates" icon="updates" title="Updates" detail={unreadUpdates > 0 ? `${unreadUpdates} unread` : ""} />
        <ActionRow href="/my-wvcea/learning" icon="note" title="Learning records" detail={notedItems.length > 0 ? `${notedItems.length} ${notedItems.length === 1 ? "session" : "sessions"}` : ""} />
        <ActionRow href="/my-wvcea/conference-review" icon="review" title="Convention Wrap-Up" detail="" />
        <ActionRow href="/my-wvcea/recordings" icon="microphone" title="My Recordings" detail="" />
        <ActionRow href="/my-wvcea/materials" icon="document" title="Session materials" detail={resourceCount > 0 ? `${resourceCount} ${resourceCount === 1 ? "file" : "files"}` : ""} />
        <ActionRow href="/my-wvcea/surveys" icon="survey" title="Convention surveys" detail="" />
        <ExportNotesRow guide={guide} privateNotes={privateNotes}
          attendeeName={accountState.status === "signed-in" ? accountState.session.displayName : null} />
        <ActionRow href="/my-wvcea/information" icon="info" title="Convention information" detail="" />
      </nav>
    </div>
  );
}

/** The wrap-up summary, which several pages want and none of them own. */
function useConferenceReviewSummary(accountState: AttendeeAccountState, eventSlug: string): AttendeeConferenceReviewResponse | null {
  const [review, setReview] = useState<AttendeeConferenceReviewResponse | null>(null);
  useEffect(() => {
    if (accountState.status !== "signed-in") { setReview(null); return; }
    const controller = new AbortController();
    void fetch(`/api/attendee/events/${encodeURIComponent(eventSlug)}/conference-review`, { credentials: "same-origin", signal: controller.signal })
      .then(async (response) => { if (response.ok) setReview(await response.json() as AttendeeConferenceReviewResponse); })
      .catch(() => { /* Every page that asks for this still works without it. */ });
    return () => controller.abort();
  }, [accountState.status, eventSlug]);
  return review;
}

/** The learning records, which used to be a tab inside My WVCEA. */
/**
 * Her shelf.
 *
 * This screen was built from the notes on her own phone: it listed the sessions she had typed
 * something into and nothing else. The companion the software writes for her — from what she
 * recorded, scanned, noted or saved — was reachable only by opening the one session it
 * belonged to, and no route in the app led there. A teacher who recorded a seminar and typed
 * nothing opened this page, saw it empty, and concluded the recording had been for nothing.
 *
 * So it asks the server what she has, and falls back to the notes on the phone when it cannot
 * be reached. A shelf that is briefly short is better than one that is empty.
 */
function LearningRecords({ guide, privateNotes, accountState }: { guide: PublicConventionGuide; privateNotes: ReturnType<typeof usePrivateNotes>; accountState: AttendeeAccountState }) {
  useDocumentTitle(`Learning records · ${guide.event.name}`);
  const signedIn = accountState.status === "signed-in";
  const [library, setLibrary] = useState<{ loading: boolean; entries: readonly AttendeeLearningLibraryEntry[] }>({ loading: signedIn, entries: [] });
  useEffect(() => {
    if (!signedIn) {
      setLibrary({ loading: false, entries: [] });
      return;
    }
    const controller = new AbortController();
    void fetch(`/api/attendee/events/${encodeURIComponent(guide.event.slug)}/learning-records`, {
      credentials: "same-origin",
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) return setLibrary({ loading: false, entries: [] });
      const body = await response.json() as { entries?: readonly AttendeeLearningLibraryEntry[] };
      setLibrary({ loading: false, entries: body.entries ?? [] });
    }).catch(() => {
      // Her own notes are on the phone and still worth showing.
      setLibrary({ loading: false, entries: [] });
    });
    return () => controller.abort();
  }, [guide.event.slug, signedIn]);

  const noteFor = (itemId: string) => {
    const note = privateNotes.notes[itemId];
    return note && (note.values.body.trim() || note.values.markers.length > 0 || note.conflict) ? note : undefined;
  };
  const entryFor = new Map(library.entries.map((entry) => [entry.itemId, entry]));
  // Server order first — newest companion at the top — then any session she noted and the
  // software has not written for. One row per session either way.
  const rows = guide.items
    .map((item) => ({ item, entry: entryFor.get(item.id), note: noteFor(item.id) }))
    .filter((row) => row.entry || row.note)
    .sort((one, other) => (other.entry?.updatedAt ?? "").localeCompare(one.entry?.updatedAt ?? ""));

  return (
    <div className="page personal-page">
      <SubpageHeader href="/my-wvcea" label={personalWorkspaceName()} />
      <PageIntro title="Learning records" />
      {library.loading ? (
        <section className="personal-empty"><span className="empty-icon">…</span><h2>Gathering your records…</h2></section>
      ) : rows.length === 0 ? (
        <section className="personal-empty notes-empty"><span className="empty-icon">✎</span><Link className="primary-action" href="/schedule">Find a session</Link></section>
      ) : (
        <div className="personal-note-list">{rows.map(({ item, entry, note }) => <LearningRow key={item.id} item={item} entry={entry} note={note} />)}</div>
      )}
      {signedIn && rows.length > 0 && <Link className="conference-review-entry" href="/my-wvcea/conference-review"><span aria-hidden="true">◎</span><span><small>Across the convention</small><strong>My Convention Wrap-Up</strong></span><b aria-hidden="true">›</b></Link>}
    </div>
  );
}

/**
 * One session on the shelf.
 *
 * Three states, told apart in the small line above the title: a record she has saved is
 * hers, a record the software wrote is waiting for her to read, and a session she only
 * typed a note into is a note. She never has to know which table it came out of, but the
 * difference between "yours" and "written for you" is the one she will act on.
 */
export function LearningRow({ item, entry, note }: {
  item: PublicScheduleItem;
  entry: AttendeeLearningLibraryEntry | undefined;
  note: ReturnType<typeof usePrivateNotes>["notes"][string] | undefined;
}) {
  /*
   * A record she has is never a warning.
   *
   * This row borrowed the private note's styling, and with it the beige mark and the words
   * "Needs review" that a note wears when the phone and the server disagree about it. A
   * companion the software wrote is not in that state and never was: every row on the screen
   * came up looking like a problem. The warning is kept for what it was built for — a note
   * with nothing written for it yet.
   */
  const conflict = Boolean(note?.conflict) && !entry;
  const label = conflict ? "Needs review"
    : entry?.kept ? "Your record"
    : entry ? "Written for you"
    : `${note?.values.markers.length ?? 0} marked ${(note?.values.markers.length ?? 0) === 1 ? "item" : "items"}`;
  const preview = entry?.centralIdea.trim()
    || note?.values.body.trim()
    || note?.values.markers.find((marker) => marker.text.trim())?.text
    || "Open this to read what was said.";
  return (
    <Link className={`personal-note-row${conflict ? " needs-review" : ""}`} href={`/my-wvcea/learning/${encodeURIComponent(item.id)}`}>
      <span className="personal-note-mark" aria-hidden="true">{conflict ? "!" : entry ? "◉" : "✎"}</span>
      <span><small>{label}</small><strong>{item.title}</strong><p>{preview}</p></span>
      <b aria-hidden="true">›</b>
    </Link>
  );
}


/** The presenter files, which used to be the tab called Resources. */
function SessionMaterialsPage({ guide, resourceState, signedIn }: { guide: PublicConventionGuide; resourceState: ReturnType<typeof useSessionResources>; signedIn: boolean }) {
  useDocumentTitle(`Session materials · ${guide.event.name}`);
  return (
    <div className="page personal-page">
      <SubpageHeader href="/my-wvcea" label={personalWorkspaceName()} />
      <PageIntro title="Session materials" />
      <MyResources guide={guide} resourceState={resourceState} signedIn={signedIn} />
    </div>
  );
}

function MyResources({ guide, resourceState, signedIn }: { guide: PublicConventionGuide; resourceState: ReturnType<typeof useSessionResources>; signedIn: boolean }) {
  if (resourceState.status === "loading") return <section className="personal-empty"><span className="empty-icon">…</span><h2>Gathering presenter resources…</h2></section>;
  if (resourceState.status === "unavailable") return <section className="personal-empty"><span className="empty-icon">!</span><h2>Resources could not be loaded.</h2><p>Your saved sessions and notes still work.</p><button className="primary-action" type="button" onClick={resourceState.retry}>Try again</button></section>;
  const sessions = guide.items.map((item) => ({ item, resources: resourcesForItem(resourceState, item.id) })).filter((entry) => entry.resources.length > 0);
  if (sessions.length === 0) return <section className="personal-empty"><span className="empty-icon"><AppIcon name="document" /></span><h2>No presenter resources yet.</h2><p>Handouts, slides, and links will collect here when the association releases them.</p></section>;
  return <div className="personal-resource-list">{sessions.map(({ item, resources }) => <section key={item.id}><Link href={`/schedule/${encodeURIComponent(item.id)}`}><span><small>Session resources</small><strong>{item.title}</strong></span><b aria-hidden="true">›</b></Link><SessionResources resources={resources} signedIn={signedIn} eventSlug={guide.event.slug} embedded /></section>)}</div>;
}

/**
 * The block the clock has reached, sitting at its own place in the rail rather than
 * in a card above it. It is the only row that carries actions, because it is the
 * only one somebody needs to act on while standing in a hallway.
 */
function FocusCard({ row, now, timeZone, eventSlug, checkIn }: {
  row: DayTimelineRow; now: Date; timeZone: string; eventSlug: string;
  /** Whether this session has a QR code in the room — a seminar, running now. */
  checkIn: boolean;
}) {
  const progress = blockProgress(row, now, timeZone);
  const running = row.state === "now";
  return (
    <div className="rail-focus">
      <div className="rail-focus-card">
        <header>
          <p>{running ? "Happening now" : "Next up"}</p>
          <span className="rail-focus-head-actions">
            {/* The same corner as the session page, at the same moment: she is in the
                room, and this is the screen most people open first. */}
            {running && checkIn && row.itemId && <CheckInButton eventSlug={eventSlug} itemId={row.itemId} />}
            {row.chosen && <span className="rail-focus-mark" aria-hidden="true"><AppIcon name="bookmark" /></span>}
          </span>
        </header>
        {/* "Your session" only when it really is theirs. A general session or lunch
            belongs to everybody, and claiming it for them would be a small lie. */}
        <h3>{row.awaitingChoice ? row.blockTitle : row.title}</h3>
        <p className="rail-focus-when">{formatClock12(row.startTime)}{row.roomLabel ? ` · ${row.roomLabel}` : ""}</p>
        {row.presenter && <p className="rail-focus-who">{row.presenter}</p>}
        <div className="rail-focus-actions">
          <Link className="rail-focus-primary" href={row.href}>{row.awaitingChoice ? "Choose a session" : "View details"}</Link>
        </div>
      </div>
      {/* Only while a block is actually running, where it answers "how long have I got". */}
      {progress !== null && (
        <div className="rail-focus-progress" role="img" aria-label={`${Math.round(progress * 100)}% through ${row.blockTitle}`}>
          <i style={{ inlineSize: `${Math.max(4, Math.round(progress * 100))}%` }} />
        </div>
      )}
    </div>
  );
}

/** The rail's gutter is narrow, so the half of the day is left to the row beside it. */
/** "10:00 AM", which is the clock the mockup's rail keeps. */
function formatClock12(value: string): string {
  const [hourText = "0", minute = "00"] = value.split(":");
  const hour = Number(hourText);
  return `${hour % 12 || 12}:${minute} ${hour < 12 ? "AM" : "PM"}`;
}

function formatClock(value: string): string {
  const [hourText = "0", minute = "00"] = value.split(":");
  const hour = Number(hourText);
  return `${hour % 12 || 12}:${minute}`;
}

function More({ guide, accountState, unreadUpdates }: { guide: PublicConventionGuide; accountState: AttendeeAccountState; unreadUpdates: number }) {
  const presentation = associationPresentation();
  useDocumentTitle(`More · ${guide.event.name}`);
  const push = usePushNotifications(accountState.status === "signed-in" ? accountState.session : null, guide.event.slug);
  return (
    <div className="page more-page">
      <PageIntro title="More" />
      <section className="notification-setting" aria-labelledby="notification-setting-title">
        <div className="notification-setting-icon" aria-hidden="true">◌</div>
        <div><p>Convention notifications</p><h2 id="notification-setting-title">{pushTitle(push.state.status)}</h2><span>{pushDetail(push.state)}</span></div>
        {push.state.status === "off" && <button type="button" onClick={() => void push.enable()}>Turn on</button>}
        {push.state.status === "on" && <button type="button" onClick={() => void push.disable()}>Turn off</button>}
        {push.state.status === "busy" && <button type="button" disabled>{push.state.action === "enable" ? "Turning on…" : "Turning off…"}</button>}
        {push.state.status === "signed_out" && <Link href="/account">Sign in</Link>}
      </section>
      <nav className="menu-list" aria-label="More convention information">
        <ActionRow href="/updates" icon="updates" title="Official updates" detail={unreadUpdates > 0 ? `${unreadUpdates} unread ${unreadUpdates === 1 ? "message" : "messages"}` : ""} />
        {/* Help lived only in the top bar, so taking the bar off the phone would have
            left nobody a way to it. */}
        <ActionRow href="/help" icon="info" title="Help" detail="" />
        <ActionRow href="/privacy" icon="info" title="Privacy" detail={presentation.wvcea ? "What WVCEA holds about you" : "What this association has published"} />
        <ActionRow href="/account" icon="person" title={accountState.status === "signed-in" ? accountState.session.displayName : "Your account"} detail={accountState.status === "signed-in" ? "Registration and device access" : "Connect with your registration email"} />
        <ActionRow href="/more/presenters" icon="people" title="Presenters" detail={`${guide.presenters.length} presenters and meeting leaders`} />
        {guide.availability.exhibitors && <ActionRow href="/more/exhibitors" icon="people" title="Exhibitors & sponsors" detail="" />}
        <ActionRow href="/more/rooms" icon="rooms" title="Room directory" detail={`${guide.rooms.length} convention rooms`} />
        <ActionRow href="/more/event" icon="info" title="Event details" detail="" />
      </nav>
    </div>
  );
}

function pushTitle(status: ReturnType<typeof usePushNotifications>["state"]["status"]): string {
  if (status === "on") return "Updates can reach this device";
  if (status === "off") return "Stay current during the convention";
  if (status === "blocked") return "Notifications are blocked";
  if (status === "unavailable") return "Notifications are not available yet";
  if (status === "signed_out") return "Sign in to connect this device";
  if (status === "error") return "Settings need attention";
  return "Checking this device…";
}

function pushDetail(state: ReturnType<typeof usePushNotifications>["state"]): string {
  if (state.status === "on") return "Official changes can appear even when the app is closed.";
  if (state.status === "blocked") return "Allow notifications in your browser or device settings, then return here.";
  if (state.status === "unavailable") return "The Updates center still holds every official message.";
  if (state.status === "signed_out") return "";
  if (state.status === "error") return state.message;
  return "Push is optional. The in-app Updates center remains the official record.";
}

function Presenters({ guide }: { guide: PublicConventionGuide }) {
  useDocumentTitle(`Presenters · ${guide.event.name}`);
  const [query, setQuery] = useState("");
  const normalized = query.trim().toLocaleLowerCase();
  const presenters = guide.presenters.filter((presenter) => `${presenter.displayName} ${presenter.credentials ?? ""}`.toLocaleLowerCase().includes(normalized));
  return (
    <div className="page directory-page">
      <SubpageHeader href="/more" label="More" />
      <PageIntro title="Presenters" />
      <label className="search-field"><span className="visually-hidden">Search presenters</span><AppIcon name="search" /><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search presenters" /></label>
      <p className="directory-count" aria-live="polite">{normalized ? `${presenters.length} ${presenters.length === 1 ? "person" : "people"}` : ""}</p>
      <div className="directory-list">{presenters.map((presenter) => <Link key={presenter.id} className="directory-row" href={`/more/presenters/${encodeURIComponent(presenter.id)}`}>{presenter.headshotUrl ? <img className="person-headshot" src={presenter.headshotUrl} alt="" /> : <span className="person-initials" aria-hidden="true">{initials(presenter.displayName)}</span>}<span><strong>{presenter.displayName}</strong>{presenter.credentials && <small>{presenter.credentials}</small>}</span><b aria-hidden="true">›</b></Link>)}</div>
    </div>
  );
}

function PresenterDetail({ guide, presenterId, savedItemIds, updateSavedItemIds }: { guide: PublicConventionGuide; presenterId: string; savedItemIds: readonly string[]; updateSavedItemIds: (ids: readonly string[]) => void }) {
  const presenter = guide.presenters.find((candidate) => candidate.id === presenterId);
  useDocumentTitle(`${presenter?.displayName ?? "Presenter"} · ${guide.event.name}`);
  if (!presenter) return <NotFound />;
  const appearances = presenter.appearanceItemIds.map((id) => guide.items.find((item) => item.id === id)).filter(isItem);
  const saved = new Set(savedItemIds);
  return (
    <div className="page presenter-page">
      <SubpageHeader href="/more/presenters" label="Presenters" />
      <header className="presenter-profile">{presenter.headshotUrl ? <img className="profile-headshot" src={presenter.headshotUrl} alt={`${presenter.displayName} headshot`} /> : <span className="profile-initials" aria-hidden="true">{initials(presenter.displayName)}</span>}<div><h1 data-page-heading tabIndex={-1}>{presenter.displayName}</h1>{presenter.credentials && <p>{presenter.credentials}</p>}</div></header>
      {presenter.biography && <section className="presenter-biography"><h2>About</h2><p>{presenter.biography}</p></section>}
      <section><div className="section-heading"><h2>Convention appearances</h2><span>{appearances.length}</span></div><div className="directory-list">{appearances.map((item) => <ScheduleItemLink key={item.id} guide={guide} item={item} saved={saved.has(item.id)} onToggleSaved={() => updateSavedItemIds(toggleSavedItem(guide, savedItemIds, item))} hidePresenterId={presenter.id} />)}</div></section>
    </div>
  );
}

function Rooms({ guide }: { guide: PublicConventionGuide }) {
  useDocumentTitle(`Rooms · ${guide.event.name}`);
  const groups = [
    { title: "Anderson Hall", rooms: guide.rooms.filter((room) => room.code === "CHAPEL" || room.code.startsWith("AN ")) },
    { title: "Servant Center", rooms: guide.rooms.filter((room) => room.code.startsWith("SC ")) },
  ];
  return (
    <div className="page directory-page">
      <SubpageHeader href="/more" label="More" />
      <PageIntro title="Room directory" />
      <div className="room-groups">{groups.map((group) => <section key={group.title}><h2>{group.title}</h2><div className="directory-list">{group.rooms.map((room) => <Link key={room.id} className="directory-row room-row" href={`/map?room=${encodeURIComponent(room.code)}&from=/more/rooms`}><span className="room-icon" aria-hidden="true">{room.code.slice(0, 2)}</span><span><strong>{roomLabel(room)}</strong>{roomLabel(room) === room.name ? null : <small>{room.name}</small>}</span><b aria-hidden="true">›</b></Link>)}</div></section>)}</div>
    </div>
  );
}

function EventDetails({ guide }: { guide: PublicConventionGuide }) {
  useDocumentTitle(`Event details · ${guide.event.name}`);
  return (
    <div className="page event-details-page">
      <SubpageHeader href="/more" label="More" />
      <PageIntro title="Event details" />
      <dl className="event-detail-list">
        <div><dt>Convention</dt><dd>{guide.event.name}</dd></div>
        <div><dt>Dates</dt><dd>{formatEventDates(guide.event.startDate, guide.event.endDate)}</dd></div>
        <div><dt>Venue</dt><dd>{guide.event.venueName}<span>{guide.event.streetAddress ? `${guide.event.streetAddress}, ${guide.event.city}, ${guide.event.state} ${guide.event.postalCode ?? ""}` : `${guide.event.city}, ${guide.event.state}`}</span></dd></div>
        <div><dt>Theme</dt><dd>{guide.event.themePrimary}<span>{guide.event.themeSecondary}</span></dd></div>
        <div><dt>Attendee parking</dt><dd>Lots 1–5<span>Accessible spaces and routes have not been supplied.</span></dd></div>
      </dl>
      {guide.event.latitude !== null && guide.event.longitude !== null && <a className="secondary-action" href={appleDirectionsUrl(guide.event.venueName, guide.event.latitude, guide.event.longitude)} target="_blank" rel="noreferrer"><AppIcon name="map" /><span>Get directions to campus</span><b aria-hidden="true">↗</b></a>}
      <Link className="secondary-action" href="/map"><AppIcon name="map" /><span>Open the interactive map</span><b aria-hidden="true">›</b></Link>
    </div>
  );
}

/**
 * The practical questions a teacher arrives with.
 *
 * Everything on it is a record that already exists — the venue, the dates, the theme,
 * the buildings and their rooms, the parking lots, the address that reaches a person.
 * Three things WVCEA has not supplied are simply absent: where the registration desk
 * is, a telephone number, and which parking spaces are accessible. The page does not
 * say "to be confirmed" in their place, because that tells somebody nothing the silence
 * had not already told them, and a guessed desk sends a teacher to the wrong end of a
 * campus.
 *
 * Times are not repeated here. The schedule holds those, and a fact kept in two places
 * becomes two facts the day one of them changes.
 */
function ConventionInformation({ guide }: { guide: PublicConventionGuide }) {
  const presentation = associationPresentation();
  useDocumentTitle(`Convention information · ${guide.event.name}`);
  const chapel = guide.rooms.filter((room) => room.code === "CHAPEL" || room.code.startsWith("AN "));
  const servant = guide.rooms.filter((room) => room.code.startsWith("SC "));
  const directions = guide.event.latitude !== null && guide.event.longitude !== null
    ? appleDirectionsUrl(guide.event.venueName, guide.event.latitude, guide.event.longitude)
    : null;
  return (
    <div className="page information-page">
      <SubpageHeader href="/my-wvcea" label={personalWorkspaceName()} />
      <header className="detail-hero">
        <p className="detail-label">{guide.event.city}, {guide.event.state}</p>
        <h1 data-page-heading tabIndex={-1}>{guide.event.venueName}</h1>
        <p className="detail-time">{formatEventDates(guide.event.startDate, guide.event.endDate)}, {new Date(`${guide.event.startDate}T12:00:00Z`).getUTCFullYear()}</p>
        <p className="detail-presenters">{guide.event.themePrimary}</p>
        <div className="detail-hero-facts">
          {directions && <a className="hero-chip" href={directions} target="_blank" rel="noreferrer">Directions <b aria-hidden="true">↗</b></a>}
          <Link className="hero-chip" href="/map">Campus map <b aria-hidden="true">›</b></Link>
        </div>
      </header>
      <h2 className="information-group">Getting here</h2>
      <nav className="menu-list" aria-label="Getting here">
        <ActionRow href="/map?view=parking&from=/more/event" icon="parking" title="Parking" detail="Attendee lots 1–5" />
        <ActionRow href="/map?view=campus&from=/more/event" icon="map" title="Campus map" detail="" />
      </nav>
      <h2 className="information-group">Where things happen</h2>
      <nav className="menu-list" aria-label="Buildings">
        <ActionRow href="/map?view=anderson&from=/more/event" icon="rooms" title="Anderson Hall" detail={`Chapel and ${chapel.length - 1} seminar rooms`} />
        <ActionRow href="/map?view=servant-1&from=/more/event" icon="rooms" title="Servant Center" detail={`${servant.length} seminar rooms`} />
      </nav>
      <h2 className="information-group">If something goes wrong</h2>
      <nav className="menu-list" aria-label="Help">
        <ActionRow href="/help" icon="info" title="Questions" detail={presentation.wvcea ? `${HELP_ANSWERS.length} answers` : ""} />
        {presentation.wvcea && <a href={`mailto:${WVCEA_EMAIL}`}><span className="action-icon"><AppIcon name="messages" /></span><span><strong>Write to WVCEA</strong><small>{WVCEA_EMAIL}</small></span><b aria-hidden="true">↗</b></a>}
      </nav>
    </div>
  );
}

/* A heading and nothing else. Each of these pages used to carry a line under the title
   describing what the page was for, which is the application explaining itself to
   somebody already looking at it. */
function PageIntro({ title }: { title: string }) {
  return <section className="page-intro"><h1 data-page-heading tabIndex={-1}>{title}</h1></section>;
}

function SubpageHeader({ href, label }: { href: string; label: string }) {
  return <Link className="back-link subpage-back" href={href}>← {label}</Link>;
}

function LoadingShell() {
  return <div className="app-shell"><header className="topbar"><span className="brand"><BrandLockup size="small" className="brand-lockup-header" /></span></header><main><section className="state-page" aria-busy="true" aria-live="polite"><h1>Loading the convention guide…</h1><p>Reading the published schedule.</p></section></main></div>;
}

function ErrorShell({ message, retry }: { message: string; retry: () => void }) {
  useDocumentTitle(`Guide unavailable · ${associationPresentation().appName}`);
  return <div className="app-shell"><header className="topbar"><Link className="brand" href="/"><BrandLockup size="small" className="brand-lockup-header" /></Link></header><main><section className="state-page error" role="alert"><h1 data-page-heading tabIndex={-1}>The official guide is unavailable.</h1><p>{message}</p><button type="button" onClick={retry}>Try again</button></section></main></div>;
}

function NotFound() {
  useDocumentTitle(`Page not found · ${associationPresentation().appName}`);
  return <section className="state-page"><h1 data-page-heading tabIndex={-1}>This page does not exist.</h1><p>Return to the official convention schedule.</p><Link className="button-link" href="/schedule">View schedule</Link></section>;
}

function conventionPosition(guide: PublicConventionGuide, now: Date) {
  const dayById = new Map(guide.days.map((day) => [day.id, day]));
  return findConventionPosition(guide.blocks.map((block) => ({ id: block.id, date: dayById.get(block.dayId)?.date ?? "", startTime: block.startTime, endTime: block.endTime })), guide.event.timeZone, now);
}

function appleDirectionsUrl(label: string, latitude: number, longitude: number): string {
  const url = new URL("https://maps.apple.com/");
  url.searchParams.set("daddr", `${latitude},${longitude}`);
  url.searchParams.set("q", label);
  return url.toString();
}
/** The tab word stays plain. The heading says where the event actually is. */
function myWvceaHeading(): string {
  return personalWorkspaceName();
}

/** Says "a.m." once when both ends share it: 10:00–11:15 a.m., 11:30 a.m.–12:15 p.m. */
function formatTimeRange(start: string, end: string): string {
  const from = formatTime(start);
  const to = formatTime(end);
  const suffix = from.slice(-4);
  return suffix === to.slice(-4) ? `${from.slice(0, -5)}–${to}` : `${from}–${to}`;
}
function formatTime(value: string): string { return twelveHour(value); }
/**
 * The store page, with the convention named once it is known.
 *
 * Drawn immediately from what the document already carries — the application's name and its
 * icon are attributes on the page the Worker served — and the convention's name and dates
 * arrive a moment later. A teacher on a corridor's worth of signal sees the badges at once
 * and the line above them fills in; she never waits on a fetch to be shown the way in.
 */
function StoreLandingPage() {
  const presentation = associationPresentation();
  const [event, setEvent] = useState<{ name: string; dates: string | null } | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    void fetch("/api/public/bootstrap", { credentials: "same-origin", signal: controller.signal })
      .then(async (response) => (response.ok ? response.json() as Promise<{ event?: { name?: string; startDate?: string; endDate?: string; venueName?: string } }> : null))
      .then((body) => {
        const found = body?.event;
        if (!found?.name || controller.signal.aborted) return;
        setEvent({
          name: found.name,
          dates: found.startDate && found.endDate
            ? `${formatEventDates(found.startDate, found.endDate)}${found.venueName ? ` · ${found.venueName}` : ""}`
            : null,
        });
      })
      .catch(() => { /* The badges are the page. A name is a courtesy. */ });
    return () => controller.abort();
  }, []);
  useDocumentTitle(presentation.appName);
  return <StoreLanding appName={presentation.appName} eventName={event?.name ?? ""} dates={event?.dates ?? null} />;
}

function formatEventDates(startDate: string, endDate: string): string {
  const start = new Date(`${startDate}T12:00:00Z`);
  const end = new Date(`${endDate}T12:00:00Z`);
  const month = new Intl.DateTimeFormat("en-US", { month: "long", timeZone: "UTC" }).format(start);
  return `${month} ${start.getUTCDate()}–${end.getUTCDate()}`;
}
/** "Thursday, September 17" becomes "Thursday 17", which is what fits on a pill. */
function dayPillLabel(label: string): string {
  const [weekday = label, rest = ""] = label.split(",");
  const date = rest.trim().split(" ").pop();
  return date ? `${weekday.trim()} ${date}` : weekday.trim();
}

function scheduleFiltersFromSearch(search: string, firstDay: string) {
  const params = new URLSearchParams(search);
  // `block` is where somebody asked to be, rather than what they are filtering by: the
  // day rail names a block and expects to land on it open.
  return { day: params.get("day") ?? firstDay, query: params.get("q") ?? "", audience: params.get("audience") ?? "", room: params.get("room") ?? "", kind: params.get("kind") ?? "", block: params.get("block") ?? "" };
}
function initials(name: string): string { return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join(""); }
function useDocumentTitle(title: string): void { useEffect(() => { document.title = title; }, [title]); }
function isString(value: unknown): value is string { return typeof value === "string"; }
function isItem(value: PublicScheduleItem | undefined): value is PublicScheduleItem { return Boolean(value); }
function isPresenter(value: PublicConventionGuide["presenters"][number] | undefined): value is PublicConventionGuide["presenters"][number] { return Boolean(value); }
